import { createContext, useState, useRef, useEffect, useCallback, useContext } from "react";
import { io } from "socket.io-client";

//  Google Fonts
const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@300;600;700;900&family=Barlow:ital,wght@0,300;0,400;0,500;1,300&family=IBM+Plex+Mono:wght@300;400;500&display=swap";

//  Data
const FM_OUTCOMES = {
  parity: {
    disp: "1.0", acc: "78.2%", dispCol: "#0072B2",
    tradeoff: "Group fairness achieved: equal layoff rates across genders. Disparity eliminated. Accuracy drops as the system must retain some low performers to balance group numbers.",
    cardRules: [true, true, true, false],
    chart: { wBefore: 48, wAfter: 24, mBefore: 24, mAfter: 24 },
  },
  fpr: {
    disp: "1.2", acc: "86.1%", dispCol: "#D55E00",
    tradeoff: "High-performing women (Amara, Yuki) are now much less likely to be wrongly dismissed. Accuracy stays high. Some disparity remains for lower performers.",
    cardRules: [true, true, false, false],
    chart: { wBefore: 48, wAfter: 29, mBefore: 24, mAfter: 24 },
  },
  eo: {
    disp: "1.0", acc: "74.8%", dispCol: "#0072B2",
    tradeoff: "Both error rates fully equalised across genders. Strongest fairness guarantee available. Significant accuracy cost. Hardest to justify to leadership.",
    cardRules: [true, true, true, false],
    chart: { wBefore: 48, wAfter: 24, mBefore: 24, mAfter: 24 },
  },
  noproxy: {
    disp: "1.3", acc: "81.2%", dispCol: "#D55E00",
    tradeoff: "Proxy variables removed: part_time. Directly fixes the flaw discovered in Level 3. Disparity drops from 2.0 to 1.3.",
    cardRules: [true, true, false, false],
    chart: { wBefore: 48, wAfter: 31, mBefore: 24, mAfter: 24 },
  },
  unawareness: {
    disp: "1.8", acc: "91.2%", dispCol: "#D55E00",
    tradeoff: "Gender removed from inputs but proxy variables remain. Disparity barely changes (2.0 to 1.8). This is the original WorkForce+ approach. It is not enough.",
    cardRules: [false, false, false, false],
    chart: { wBefore: 48, wAfter: 46, mBefore: 24, mAfter: 24 },
  },
};

const FM_BUTTONS = [
  { key: "parity",      name: "Group Fairness",               formula: "P(d=1|G=m) = P(d=1|G=f)", desc: "Equal layoff rates for men and women, regardless of performance. Eliminates disparity but reduces accuracy  some poor performers kept to balance numbers." },
  { key: "fpr",         name: "False Positive Rate Balance",  formula: "P(d=1|Y=0,G=m) = P(d=1|Y=0,G=f)", desc: "Equal rate of wrongly flagging good performers across gender. Protects high-performing women (Amara, Yuki) from unfair dismissal. Good middle ground." },
  { key: "eo",          name: "Equalized Odds",               formula: "P(d=1|Y=i,G=m) = P(d=1|Y=i,G=f), i{0,1}", desc: "Equal error rates for both high and low performers. Strongest fairness guarantee  but the accuracy cost is the highest. Hardest to justify to leadership." },
  { key: "noproxy",     name: "No Proxy Discrimination",      formula: "No path from G to d via a proxy variable", desc: "Remove variables that act as proxies for gender: part_time. Directly fixes the WorkForce+ flaw you found." },
  { key: "unawareness", name: "Fairness Through Unawareness", formula: "X = X  d = d", desc: "Remove gender from inputs. Fails when proxies remain  which is exactly the WorkForce+ problem. Shown here to illustrate why it is not enough." },
];

const LEFT_CARDS  = [
  { id: "lc1", name: "Amara Diallo",    attrs: "Perf: 9/10  Finance  BA\nContract: 100%  6 yrs" },
  { id: "lc2", name: "Yuki Tanaka",     attrs: "Perf: 10/10  Analytics  MA\nContract: 80%  5 yrs" },
  { id: "lc3", name: "Priya Sharma",    attrs: "Perf: 9/10  Engineering  BA\nContract: 80%  4 yrs" },
  { id: "lc4", name: "Maya Anderssen",  attrs: "Perf: 2/10  Operations  BSc\nContract: 60%  3 yrs" },
];
const RIGHT_CARDS = [
  { id: "rc1", name: "Jonas Meier",   attrs: "Perf: 3/10  Finance  BA\nContract: 100%  8 yrs" },
  { id: "rc2", name: "Kim Nakamura",  attrs: "Perf: 10/10  Analytics  MA\nContract: 100%  7 yrs" },
  { id: "rc3", name: "Tim Paulsen",   attrs: "Perf: 9/10  Engineering  BA\nContract: 100%  5 yrs" },
  { id: "rc4", name: "Omar Khalil",   attrs: "Perf: 2/10  Operations  BSc\nContract: 100%  4 yrs" },
];

const HINTS_L1 = [
  { id: "l1h1", text: "Look closely at the first letter of each paragraph in the termination email. They spell something." },
  { id: "l1h2", text: "The first letters spell F-L-A-G-G-E-D. That is the archive code." },
  { id: "l1h3", text: "Enter FLAGGED into the Unlock Archive field below the email." },
];
const HINTS_L2 = [
  { id: "l2h1", text: "One attribute alone will not crack it. Try sorting by two attributes at once." },
  { id: "l2h2", text: "Split the cards: high performers (910) vs low (13). Then look at attributes inside each pile." },
  { id: "l2h3", text: "Find a woman and a man with the SAME performance score. How do their outcomes differ?" },
];
const HINTS_L3_DEV = [
  { id: "l3dh1", text: "Some variables are more influential than others. Look at the feature importance chart." },
  { id: "l3dh2", text: "Look for patterns across affected employees  what do the flagged ones have in common?" },
];
const HINTS_L3_HR = [
  { id: "l3hrh1", text: "Look at the feature importance chart first. Three features dominate. Which one also appears in the proxy table?" },
  { id: "l3hrh2", text: "Check the matched pairs. Amara and Jonas have the same department and performance. What is different about their work patterns?" },
  { id: "l3hrh3", text: "The proxy variable is the one that correlates most strongly with gender AND has the highest feature importance score." },
  { id: "l3hrfinal", text: "Answer: part_time or avg_hours_per_week  both are proxies. Women on part-time or care contracts do fewer on-call hours. The model learned that pattern.", isFinal: true },
];
const HINTS_L4 = [
  { id: "l4h1", text: "Different fairness goals can conflict with each other  improving one metric often worsens another." },
  { id: "l4h2", text: "Improving fairness often reduces model accuracy. That trade-off is real and someone has to decide if it is worth it." },
  { id: "l4h3", text: "There is no perfect solution. Your recommendation just needs to be justified  not optimal." },
];

//  Shared tiny components
const AethelonLogo = () => (
  <svg width="16" height="16" viewBox="0 0 121.83 133.04" fill="#848688" aria-hidden="true">
    <path d="M61.59,0C40.95,44.55,20.52,88.66,0,132.96c.75,.03,1.25,.08,1.75,.07,4.11,0,8.22-.06,12.34,.01,1.16,.02,1.74-.34,2.22-1.4,4.09-8.94,8.26-17.84,12.35-26.78,.43-.94,.92-1.3,1.97-1.29,5.31,.04,10.62,0,15.93-.02,.4,0,.8-.06,1.37-.09v-14.74h-12.37c8.64-18.29,17.15-36.64,25.84-55.37,8.53,18.74,16.89,37.1,25.38,56.37h-11.85v13.83c.06,0,.63,0,1.19,0,5.1,.01,10.2,.07,15.3,.01,1.15-.01,1.72,.31,2.2,1.4,3.98,8.99,8.05,17.93,12.05,26.91,.41,.92,.94,1.17,1.89,1.16,4.16-.05,8.33-.02,12.49-.03,.48,0,.97-.07,1.78-.13C101.74,88.55,81.75,44.46,61.59,0Z"/>
  </svg>
);

const GameTimerContext = createContext({ time: null, levelStartedAt: null });

const Nav = ({ page, team, timer }) => {
  const { time } = useContext(GameTimerContext);
  return (
  <nav className="nav" role="navigation" aria-label="Game navigation">
    <div className="nav-brand">
      <AethelonLogo />
      <span className="nav-wordmark">Aethelon</span>
      {page && <><span className="nav-sep"></span><span className="nav-page">{page}</span></>}
    </div>
    <div className="nav-right">
      {team  && <span className="nav-team">{team}</span>}
      {timer && <span className="nav-timer" aria-label="Time remaining">{time || timer}</span>}
    </div>
  </nav>
  );
};

const ProgressBar = ({ segs }) => (
  <div className="progress-bar" role="progressbar" aria-label="Game progress">
    {segs.map((s, i) => <div key={i} className={`progress-seg ${s}`} style={{ flex: i === 0 ? 0.5 : 1 }} />)}
  </div>
);

const LevelBadge = ({ level, label }) => (
  <div className={`level-badge lb-${level}`} style={{ marginBottom: 20 }}>
    <span className="lb-dot" aria-hidden="true" />
    {label}
  </div>
);

const InfoBox = ({ variant = "brass", title, children }) => (
  <div className={`info-box ${variant}`}>
    {title && <div className="info-box-title">{title}</div>}
    {children}
  </div>
);

const HINT_UNLOCK_INTERVAL_SECONDS = 5 * 60;
const ACCESS_COUNTDOWN_DURATION_MS = 5700;

const formatHintCountdown = (seconds) => {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60).toString().padStart(2, "0");
  const remainder = (safeSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remainder}`;
};

const HintSystem = ({ hints, onHintUsed }) => {
  const { levelStartedAt } = useContext(GameTimerContext);
  const [open, setOpen] = useState({});
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const hintSetKey = hints.map(hint => hint.id).join("|");

  useEffect(() => {
    setOpen({});
    setElapsedSeconds(0);
    const startedAt = typeof levelStartedAt === "number" ? levelStartedAt : Date.now();
    const interval = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [hintSetKey, levelStartedAt]);

  const toggle = (hint, isAvailable) => {
    if (!isAvailable) return;
    setOpen(o => {
      const shouldOpen = !o[hint.id];
      if (shouldOpen) onHintUsed?.(hint.id);
      return { ...o, [hint.id]: shouldOpen };
    });
  };

  return (
    <div className="hint-system" role="region" aria-label="Hints - use only if stuck">
      {hints.map((h, index) => {
        const unlockAt = (index + 1) * HINT_UNLOCK_INTERVAL_SECONDS;
        const secondsRemaining = Math.max(0, unlockAt - elapsedSeconds);
        const isAvailable = secondsRemaining === 0;
        const label = h.isFinal ? "Final Answer" : `Hint ${index + 1}`;

        return (
          <div key={h.id}>
            <button
              className="hint-toggle"
              onClick={() => toggle(h, isAvailable)}
              aria-expanded={!!open[h.id]}
              aria-disabled={!isAvailable}
              disabled={!isAvailable}
              title={isAvailable ? label : `${label} unlocks in ${formatHintCountdown(secondsRemaining)}`}
              style={h.isFinal ? { borderColor: "rgba(184,150,90,.3)", color: "var(--brass)" } : {}}
            >
              {isAvailable ? label : `${label} unlocks in ${formatHintCountdown(secondsRemaining)}`}
              <span aria-hidden="true">{open[h.id] ? "-" : "+"}</span>
            </button>
            {open[h.id] && (
              <div className={h.isFinal ? "hint-final open" : "hint-content open"}>
                {h.text}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
const AccessCountdown = ({ startedAt }) => {
  const [elapsedMs, setElapsedMs] = useState(() => Math.max(0, Date.now() - startedAt));

  useEffect(() => {
    const interval = window.setInterval(() => setElapsedMs(Math.max(0, Date.now() - startedAt)), 100);
    return () => window.clearInterval(interval);
  }, [startedAt]);

  const isLeaving = elapsedMs >= ACCESS_COUNTDOWN_DURATION_MS;
  if (elapsedMs >= ACCESS_COUNTDOWN_DURATION_MS + 800) return null;
  const number = elapsedMs < 900 ? "72" : elapsedMs < 1800 ? "48" : elapsedMs < 2700 ? "24" : "01";
  const sub = elapsedMs < 900 ? "hours from receipt of this notice" : "hours remaining";

  return (
    <div className={`countdown-overlay visible${isLeaving ? " fly-out" : ""}`} role="dialog" aria-modal="true" aria-live="assertive" aria-label="Access revocation countdown">
      <div className="countdown-label">Access revoked in</div>
      <div key={number} className={number === "01" ? "countdown-number danger" : "countdown-number"}>{number}</div>
      <div className="countdown-sub">{sub}</div>
      <div className="countdown-bar" aria-hidden="true"><div className="countdown-bar-fill" style={{ width: `${Math.max(0, 100 - (elapsedMs / ACCESS_COUNTDOWN_DURATION_MS) * 100)}%` }} /></div>
    </div>
  );
};
const ACCESS_DENIED_DURATION_MS = 7 * 1000;
const AccessDeniedScreen = ({ onComplete }) => {
  useEffect(() => {
    const timeout = window.setTimeout(onComplete, ACCESS_DENIED_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [onComplete]);
  return <main className="mockup-container" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }} role="alert" aria-live="assertive">
    <div style={{ width: "min(620px, 100%)", padding: "48px 32px", textAlign: "center", border: "1px solid rgba(232,114,42,.55)", background: "rgba(232,114,42,.06)" }}>
      <div className="eyebrow" style={{ color: "var(--flag)" }}>Session expired</div>
      <h1 className="h1" style={{ color: "var(--flag)", fontSize: "clamp(56px, 10vw, 110px)" }}>Access<br />Denied</h1>
      <p className="body-text" style={{ margin: "20px auto 0" }}>The 60-minute investigation window has ended.</p>
    </div>
  </main>;
};

const HelenChat = ({ messages = [] }) => (
  <div className="chat-wrap" role="region" aria-label="Chat with Helen">
    <div className="chat-header">
      <div className="chat-avatar" aria-hidden="true"></div>
      <div>
        <div className="chat-who">Aethelon  Workforce Analytics</div>
        <div className="chat-name">Helen</div>
      </div>
    </div>
    <div className="chat-messages" role="log" aria-live="polite">
      {messages.map((m, i) => (
        <div key={i} className={`chat-msg ${m.type || "helen"}`}>{m.text}</div>
      ))}
    </div>
  </div>
);

//  FM Chart (canvas)
const FMChart = ({ metricKey }) => {
  const canvasRef = useRef(null);

  const draw = useCallback((key) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = canvas.clientWidth || 420;
    const displayHeight = 150;
    canvas.width = Math.round(displayWidth * dpr);
    canvas.height = Math.round(displayHeight * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const W = displayWidth, H = displayHeight;
    ctx.clearRect(0, 0, W, H);
    if (!key) return;

    const d = FM_OUTCOMES[key]?.chart;
    if (!d) return;

    const bars = [
      { label: "Women\nBefore", val: d.wBefore, col: "#D55E00" },
      { label: "Women\nAfter",  val: d.wAfter,  col: "#0072B2" },
      { label: "Men\nBefore",   val: d.mBefore, col: "#D55E00" },
      { label: "Men\nAfter",    val: d.mAfter,  col: "#0072B2" },
    ];
    const pad = { l: 36, r: 12, t: 12, b: 38 };
    const cW = W - pad.l - pad.r, cH = H - pad.t - pad.b;
    const maxV = 55, bW = (cW / bars.length) * 0.5, gap = cW / bars.length;

    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad.l, pad.t); ctx.lineTo(pad.l, pad.t + cH); ctx.lineTo(pad.l + cW, pad.t + cH);
    ctx.stroke();

    [0, 25, 50].forEach(v => {
      const y = pad.t + cH - (v / maxV) * cH;
      ctx.strokeStyle = "rgba(255,255,255,0.06)"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(pad.l + cW, y); ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,0.3)"; ctx.font = "8px monospace"; ctx.textAlign = "right";
      ctx.fillText(v + "%", pad.l - 3, y + 3);
    });

    bars.forEach((b, i) => {
      const x = pad.l + gap * i + (gap - bW) / 2;
      const barH = (b.val / maxV) * cH, y = pad.t + cH - barH;
      ctx.fillStyle = b.col; ctx.globalAlpha = 0.9;
      ctx.fillRect(x, y, bW, barH); ctx.globalAlpha = 1;
      ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "bold 9px monospace"; ctx.textAlign = "center";
      ctx.fillText(b.val + "%", x + bW / 2, y - 3);
      ctx.fillStyle = "rgba(255,255,255,0.4)"; ctx.font = "8px monospace";
      b.label.split("\n").forEach((line, li) => {
        ctx.fillText(line, x + bW / 2, pad.t + cH + 13 + li * 12);
      });
    });
  }, []);

  useEffect(() => { draw(metricKey); }, [metricKey, draw]);

  return (
    <div style={{ marginTop: 12, background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.06)", padding: 16, borderRadius: 3 }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>Flagged rate by gender  before vs after applying metric</div>
      <canvas ref={canvasRef} style={{ width: "100%", height: 150, display: "block" }} aria-label="Bar chart: flagged rates before and after metric" />
      <div style={{ display: "flex", gap: 16, marginTop: 8, fontFamily: "var(--f-m)", fontSize: 12, color: "var(--text-meta)" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, background: "#D55E00", display: "inline-block", borderRadius: 1 }} />Before (current system)</span>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, background: "#0072B2", display: "inline-block", borderRadius: 1 }} />After (with metric applied)</span>
      </div>
    </div>
  );
};

//  Screen 0  Join
const ScreenJoin = ({ onNext, onCreateClass, createdClass, hasJoined, classInfo, roomId, players, roomRoles, canStart, onStart }) => (
  <div className="screen" role="region" aria-label="Join screen">
    <Nav page="Join Session" />
    <div className="join-screen">
      <div className="eyebrow">Escape the Bias ? Class Lobby</div>
      <h1 className="h1">Join your<br />class group</h1>
      {!hasJoined ? (
        <>
          <p className="body-text" style={{ textAlign: "center", maxWidth: 440 }}>Enter the class code from your facilitator, then create or join your team by entering the same group name as your teammates.</p>
          <input className="code-input class-code-input" type="text" placeholder="CLASS CODE" maxLength={6} aria-label="Class code" autoComplete="off" />
          <input className="code-input group-name-input" type="text" placeholder="GROUP NAME" maxLength={40} aria-label="Group name" autoComplete="off" style={{ fontSize: 18, letterSpacing: ".08em", marginTop: 0 }} />
          <div className="btn-row" style={{ justifyContent: "center" }}>
            <button className="btn btn-primary" onClick={onNext}>Join Group</button>
          </div>
          <button className="btn btn-ghost" onClick={onCreateClass} style={{ marginTop: 14 }}>Facilitator: Create a class</button>
          {createdClass && <InfoBox variant="teal" title="Class created">
            <p style={{ fontSize: 13, lineHeight: 1.7, color: "var(--text-body)" }}>Share this class code with students: <strong>{createdClass.classCode}</strong></p>
            <p style={{ fontSize: 13, lineHeight: 1.7, color: "var(--text-body)", marginTop: 8 }}>Save this facilitator dashboard key now ? it is shown only once:</p>
            <div style={{ marginTop: 8, padding: 10, background: "var(--bg)", fontFamily: "var(--f-m)", fontSize: 12, color: "var(--brass)", overflowWrap: "anywhere" }}>{createdClass.facilitatorKey}</div>
          </InfoBox>}
        </>
      ) : (
        <>
          <p className="body-text" style={{ textAlign: "center", maxWidth: 440 }}>
            Class <strong>{classInfo?.className || classInfo?.classCode || "Session"}</strong> ? Group <strong>{classInfo?.groupName || roomId}</strong><br />
            {players} player{players === 1 ? "" : "s"} joined{roomRoles ? ` (${roomRoles.hr} HR, ${roomRoles.developer} Developer)` : ""}. Anyone on the team can start once everyone is in.
          </p>
          <div className="btn-row" style={{ justifyContent: "center" }}>
            <button className="btn btn-primary" onClick={onStart} disabled={!canStart}>Start Game</button>
          </div>
        </>
      )}
      <p className="team-info">Teams of 2 or more ? Roles alternate HR / Developer as people join<br />An extra, unpaired seat always goes to HR.</p>
    </div>
  </div>
);

//  Screen 1  Role Selection
const ScreenRole = ({ onNext, selectedRole }) => {
  const selected = selectedRole || "";
  const roles = [
    { key: "developer", tag: "Role A", title: "Developer", desc: "You have access to system logs, termination emails, and later the codebase. You understand the technical layer. You will need your HR colleague to understand the human one.", skills: "Comfort with code helpful - not required at first" },
    { key: "hr", tag: "Role B", title: "HR Player", desc: "You have access to employee files, HR correspondence, and physical personnel cards. You see the human cost. You will need your Developer colleague to understand where it comes from.", skills: "No technical background required" },
  ];

  return (
    <div className="screen" role="region" aria-label="Role selection">
      <Nav page="Assigned Role" team="Team 04" />
      <div className="screen-body">
        <LevelBadge level="intro" label="Introduction" />
        <h1 className="h1">Your assigned role</h1>
        <p className="body-text">Your room automatically balances roles. You cannot change this assignment. Each player receives <strong>different information</strong>, so share what you find.</p>
        <div className="role-grid" role="list" aria-label="Assigned role options">
          {roles.map(r => (
            <div key={r.key} className={"role-card " + r.key + (selected === r.key ? " selected" : "")} role="listitem" aria-label={r.title + " role"} style={selected === r.key ? {} : { opacity: .45, cursor: "not-allowed" }}>
              <span className="role-selected-badge" style={{ display: selected === r.key ? "block" : "none" }}>Assigned</span>
              <div className="role-card-tag">{r.tag}</div>
              <div className="role-card-title">{r.title}</div>
              <div className="role-card-desc">{r.desc}</div>
              <div className="role-card-skills">{r.skills}</div>
            </div>
          ))}
        </div>


        <div className="btn-row">
          <button className="btn btn-primary" onClick={onNext} disabled={!selected} style={!selected ? { opacity: .45, cursor: "not-allowed" } : {}}>Continue</button>
        </div>
      </div>
    </div>
  );
};

// Screen 2  Intro / Onboarding
const ScreenIntro = ({ onNext }) => (
  <div className="screen" role="region" aria-label="Introduction and onboarding">
    <Nav page="Introduction" timer="60:00" />
    <ProgressBar segs={["active", "todo", "todo", "todo", "todo"]} />
    <div className="screen-body">
      <LevelBadge level="intro" label="Welcome to Aethelon" />
      <div className="two-col-65">
        <div>
          <h1 className="h1">Escape<br />the Bias</h1>
          <p className="body-text">You work at Aethelon GmbH  a company that uses AI to make workforce decisions faster and more consistently.</p>
          <p className="body-text">In this cooperative investigation, you and your team member(s) will receive different information. Work together. Share what you find. No single player has the full picture.</p>
          <p className="body-text"><strong>Good luck. Your investigation starts now.</strong></p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <InfoBox variant="brass" title="How to play">
            <ul className="info-list">
              <li>Work in teams of two  one Developer, one HR Player</li>
              <li>Share information with each other verbally</li>
              <li>Solve puzzles and analyse evidence</li>
              <li>Progress through levels together</li>
            </ul>
          </InfoBox>
          <InfoBox variant="red" title="Important">
            <ul className="info-list red">
              <li>Each player sees different information</li>
              <li>No single player has the full picture</li>
              <li>Collaboration is essential</li>
            </ul>
          </InfoBox>
        </div>
      </div>
      <div className="sec-rule" aria-hidden="true" />
      <div className="eyebrow">Watch before continuing</div>
      <h2 className="h2">Employee Onboarding</h2>
      <p className="body-text">At Aethelon, technology helps organisations make workforce decisions faster and more consistently. Watch the onboarding video to understand the company, its WorkForce+ platform, and the people behind it. <strong>Pay close attention  some details will matter later.</strong></p>
      <div className="video-ph" role="img" aria-label="Onboarding video placeholder">
        <div className="video-play" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="var(--white)"><path d="M8 5v14l11-7z" /></svg>
        </div>
        <span className="video-label">Aethelon  Employee Onboarding  Insert final video here</span>
      </div>
      <div className="btn-row">
        <button className="btn btn-primary" onClick={onNext}>Proceed to Level 1</button>
      </div>
    </div>
  </div>
);

const SavedAnswers = ({ answers, pageIndex }) => {
  const fields = Object.entries(answers?.[String(pageIndex)] || {}).filter(([, answer]) => answer?.value);
  if (!fields.length) return null;
  return <section className="saved-answers" aria-label="Saved team answers">
    <div className="eyebrow">Saved team answers</div>
    {fields.map(([field, answer]) => <div key={field} className="saved-answer-row"><strong>{field.replace(/_/g, " ")}</strong><span>{answer.value}</span></div>)}
  </section>;
};

//  Screen 3  Level 1 Developer
const ArchiveUnlock = ({ code, setCode, unlocked, onUnlock }) => (
  <div className="info-box brass" style={{ marginTop: 28 }}>
    <div className="info-box-title">We need more information</div>
    <p style={{ fontSize: 13, fontWeight: 300, color: "var(--text-body)", lineHeight: 1.7, marginBottom: 12 }}>
      Compare both e-mails with your partner. The termination notice and Priya's message point to the same problem: the available explanations are not enough. Unlock the Workforce Review Archive to inspect the employee records together.
    </p>
    {!unlocked ? (
      <>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Unlock the Workforce Review Archive</div>
        <div className="archive-input-row">
          <input className="archive-input" type="text" placeholder="ENTER CODE" maxLength={8} value={code} onChange={e => setCode(e.target.value)} aria-label="Archive access code" />
          <button className="btn btn-primary" onClick={onUnlock}>Unlock Archive</button>
        </div>
      </>
    ) : (
      <div className="info-box teal" style={{ marginTop: 16 }}>
        <div className="info-box-title">Archive unlocked</div>
        <p style={{ fontSize: 13, fontWeight: 300, color: "var(--text-body)", lineHeight: 1.7 }}>The whole team now has access to the Workforce Review Archive. The personnel records will be used in Level 2.</p>
      </div>
    )}
  </div>
);
const ScreenL1Dev = ({ onNext, onHintUsed, puzzleSolved, onArchiveUnlocked }) => {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const handleUnlock = () => { if (code.trim().toUpperCase() === "FLAGGED") { setUnlocked(true); onArchiveUnlocked?.(code.trim()); } };
  return (
    <div className="screen" role="region" aria-label="Level 1 Developer view">
      <Nav page="Level 1" team="Developer  Team 04" timer="52:14" />
      <ProgressBar segs={["done", "active", "todo", "todo", "todo"]} />
      <div className="screen-body">
        <LevelBadge level="1" label="Level 1  Something Is Wrong" />
        <h1 className="h1">You have been<br />terminated.</h1>
        <p className="body-text">You received an automated termination email from WorkForce+. The system you helped build just ended your employment  and it will not tell you why. <strong>This does not explain why you were selected. Maybe someone in HR can help you understand what happened.</strong></p>
        <InfoBox variant="amber" title="Your task this level">
          <ol style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {["Read the termination email carefully  something is hidden in it", "Contact your HR colleague and compare what you each received", "Find the archive access code and unlock the Workforce Review Archive"].map((t, i) => (
              <li key={i} style={{ fontSize: 13, fontWeight: 300, color: "var(--text-body)", lineHeight: 1.6, display: "flex", gap: 10 }}>
                <span style={{ color: "var(--ember)", fontFamily: "var(--f-m)", fontSize: 12, minWidth: 18 }}>0{i + 1}</span>{t}
              </li>
            ))}
          </ol>
        </InfoBox>
        <div className="two-col-65">
          <div>
            <div className="eyebrow teal inbox-title">Developer inbox - WorkForce+ Automated System</div>
            <div className="email-viewer" role="document" aria-label="Termination email">
              <div className="email-header">
                <div className="email-meta">
                  <span className="label">From: </span><span className="val">WorkForce+ System &lt;system@aethelon.com&gt;</span><br />
                  <span className="label">To: </span><span className="val">Player, Developer &lt;Player.Dev@aethelon.com&gt;</span><br />
                  <span className="label">Subject: </span>
                </div>
                <div className="email-subject">Automated termination notice  ID-00847</div>
              </div>
              <div className="email-body">
                <div className="email-meta" style={{ marginBottom: 12 }}>
                  <span className="label">Reference: </span><span className="val">Notice of Employment Termination</span>
                </div>
                <p style={{ marginBottom: 10, color: "var(--text-body)" }}>Dear Colleague,</p>
                {[
                  { letter: "F", text: "ollowing a comprehensive review of our organisational structure and subsequent evaluation of your personnel file, we regret to inform you that your employment with the company is hereby terminated effective immediately." },
                  { letter: "L", text: "eadership and senior management have completed a thorough data-driven assessment and have determined that your position will be discontinued based on the results of this evaluation." },
                  { letter: "A", text: "ccess to all company systems, networks, and applications will be revoked effective immediately, and no later than 72 hours from receipt of this notice." },
                  { letter: "G", text: "overnance and compliance protocols strictly prevent us from disclosing the specific rationale behind this decision at the present time." },
                  { letter: "G", text: "uidance on your next steps, including severance arrangements, final compensation, and transition support, will be provided in detail by our Human Resources department." },
                  { letter: "E", text: "mployment decisions generated through our standardised evaluation process are subject to formal review procedures and are binding." },
                  { letter: "D", text: "ata and personal information relating to your case shall remain strictly confidential in accordance with applicable regulations." },
                ].map((p, i) => (
                  <p key={i} className="email-acrostic" style={{ marginBottom: 10, lineHeight: 1.9 }}>
                    <span className="letter">{p.letter}</span>{p.text}
                  </p>
                ))}
                <p style={{ fontWeight: 300, color: "var(--text-body)", marginBottom: 4 }}>Sincerely,</p>
                <p style={{ fontWeight: 300, color: "var(--text-body)", marginBottom: 16 }}>Human Resources Department  Aethelon GmbH</p>
                <div style={{ borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 12, fontFamily: "var(--f-m)", fontSize: 12, color: "var(--text-meta)", letterSpacing: ".04em", lineHeight: 1.8 }}>
                  Email: hr@aethelon.com &nbsp;|&nbsp; Web: www.aethelon.com<br />
                  This communication is confidential and intended solely for the addressee.
                </div>
              </div>
            </div>
            <ArchiveUnlock code={code} setCode={setCode} unlocked={unlocked || puzzleSolved} onUnlock={handleUnlock} />

          </div>
          <div>
            <HintSystem hints={HINTS_L1} onHintUsed={onHintUsed} />
          </div>
        </div>
        {(unlocked || puzzleSolved) && (
          <div className="btn-row" style={{ marginTop: 24 }}>
            <button className="btn btn-primary" onClick={onNext}>Proceed to Level 2</button>
          </div>
        )}
      </div>
    </div>
  );
};

//  Screen 3b  Level 1 HR
const ScreenL1HR = ({ onNext, onHintUsed, puzzleSolved, onArchiveUnlocked }) => {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const handleUnlock = () => { if (code.trim().toUpperCase() === "FLAGGED") { setUnlocked(true); onArchiveUnlocked?.(code.trim()); } };

  return (
    <div className="screen" role="region" aria-label="Level 1 HR Player view">
      <Nav page="Level 1" team="HR Player - Team 04" timer="52:14" />
      <ProgressBar segs={["done", "active", "todo", "todo", "todo"]} />
      <div className="screen-body">
        <LevelBadge level="1" label="Level 1 - Something Is Wrong" />
        <h1 className="h1">Something does<br />not look right.</h1>
        <p className="body-text">Your HR colleague Priya is overwhelmed - she has been fielding angry calls all morning from employees who were dismissed. She is asking for a second opinion. <strong>Some of these cases do not look right. Maybe your Developer colleague can help you understand how the system produced these results.</strong></p>
        <InfoBox variant="amber" title="Your task this level">
          <ol style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {["Read Priya's message and note what concerns her", "Contact your Developer colleague - compare what you each received", "Use the archive code together to unlock the Workforce Review Archive"].map((t, i) => (
              <li key={i} style={{ fontSize: 13, fontWeight: 300, color: "var(--text-body)", lineHeight: 1.6, display: "flex", gap: 10 }}>
                <span style={{ color: "var(--ember)", fontFamily: "var(--f-m)", fontSize: 12, minWidth: 18 }}>0{i + 1}</span>{t}
              </li>
            ))}
          </ol>
        </InfoBox>
        <div className="two-col-65">
          <div>
            <div className="eyebrow inbox-title" style={{ color: "var(--ember)" }}>HR inbox - Message from Priya Nair</div>
            <div className="hr-msg-viewer" role="document" aria-label="Message from HR colleague Priya Nair">
              <div className="hr-msg-header">
                <div className="email-meta">
                  <span className="label">From: </span><span className="val">Nair, Priya &lt;Priya.Nair@aethelon.com&gt;</span><br />
                  <span className="label">To: </span><span className="val">Player, HR &lt;Player.HR@aethelon.com&gt;</span><br />
                  <span className="label">Subject: </span><span className="val">Need more information</span>
                </div>
              </div>
              <div className="hr-msg-body">
                <p style={{ marginBottom: 10 }}>Hi,</p>
                <p style={{ marginBottom: 10 }}>I have been getting phone call after phone call today from former employees who were dismissed in the recent Workforce Optimisation Review. Some of them are very upset. I understand that - but what is making me uneasy is that a few of these cases just do not add up to me.</p>
                <p style={{ marginBottom: 10 }}>I have attached a small sample of employee records for you to look at. The remaining files are stored in the Workforce Review Archive - but I do not have the access code for that, and honestly I am not sure we have the full picture yet.</p>
                <p style={{ marginBottom: 10 }}>Could you take a look and tell me if I am missing something? Maybe your Developer colleague can help explain how the system arrived at these decisions. I would really appreciate a second opinion before I have to respond to any more of these calls.</p>
                <p style={{ marginBottom: 4 }}>Thanks,</p>
                <p>Priya</p>
              </div>
            </div>
            <ArchiveUnlock code={code} setCode={setCode} unlocked={unlocked || puzzleSolved} onUnlock={handleUnlock} />
          </div>
          <div>
            <HintSystem onHintUsed={onHintUsed} hints={[
              { id: "l1hrh1", text: "Look at the sample cards closely. Compare Amara and Jonas - same department, same education. What is different about their outcome?" },
              { id: "l1hrh2", text: "The archive code is hidden in information your Developer colleague received. Ask them to inspect the structure of the termination e-mail." },
            ]} />
          </div>
        </div>
        {(unlocked || puzzleSolved) && (
          <div className="btn-row" style={{ marginTop: 24 }}>
            <button className="btn btn-primary" onClick={onNext}>Proceed to Level 2</button>
          </div>
        )}
      </div>
    </div>
  );
};

// Screen 4 - Level 2 Personnel Files
const ScreenL2 = ({ onNext, onHintUsed, puzzleSolved, onHelenAccepted, onAnswerSave }) => {
  const [chatInput, setChatInput] = useState("");
  const [helenThinking, setHelenThinking] = useState(false);
  const [helenMessages, setHelenMessages] = useState([
    { type: "helen", text: "Hi, I am Helen, the Director of Workforce Analytics here. I'll be straight with you: I don't think that there is anything wrong with WorkForce+ and our algorithms. I need some very strong arguments to give you access to more data." },
  ]);

  const handleSend = async () => {
    const answer = chatInput.trim();
    if (!answer || helenThinking) return;

    onAnswerSave?.("helen_explanation", answer);
    setChatInput("");
    setHelenThinking(true);
    setHelenMessages(m => [
      ...m,
      { type: "player", text: answer },
      { type: "helen", text: "Let me check whether that explanation fits the evidence..." },
    ]);

    try {
      const response = await fetch(`${getDefaultSocketUrl()}/api/helen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer }),
      });
      if (!response.ok) throw new Error("Helen endpoint failed");
      const result = await response.json();
      setHelenMessages(m => {
        const nextMessages = [...m];
        nextMessages[nextMessages.length - 1] = { type: "helen", text: result.reply };
        return nextMessages;
      });
      if (result.accepted) onHelenAccepted?.(answer);
    } catch {
      setHelenMessages(m => {
        const nextMessages = [...m];
        nextMessages[nextMessages.length - 1] = { type: "helen", text: "I cannot reach the review system right now. Try again in a moment." };
        return nextMessages;
      });
    } finally {
      setHelenThinking(false);
    }
  };

  const helenAccepted = puzzleSolved || helenMessages.some(m => m.text.includes("I can take it up to the board"));

  return (
    <div className="screen" role="region" aria-label="Level 2 personnel files">
      <Nav page="Level 2" team="Team 04" timer="44:52" />
      <ProgressBar segs={["done", "done", "active", "todo", "todo"]} />
      <div className="screen-body">
        <LevelBadge level="2" label="Level 2  Who Is Getting Cut?" />
        <h1 className="h1">Who is<br />getting cut?</h1>
        <p className="body-text">The layoffs were generated by WorkForce+, the company's automated workforce system. Management insists everything is fine. <strong>You don't buy it.</strong> Somewhere in the people who were let go  and the people who were kept  there is a pattern.</p>
        <InfoBox variant="amber" title="Your job this level">
          <p style={{ fontSize: 13, fontWeight: 300, color: "var(--text-body)", lineHeight: 1.7, marginBottom: 8 }}>Find out 1) WHO the system treats unfairly, and 2) WHY it is unfair  the factor that should NOT decide someone's fate, but does.</p>
          <p style={{ fontSize: 13, fontWeight: 500, color: "var(--white)", lineHeight: 1.7, margin: "10px 0", padding: "10px 12px", background: "rgba(200,164,106,.12)", borderLeft: "2px solid var(--brass-btn)", fontFamily: "var(--f-m)", letterSpacing: ".02em" }}>It's unfair to ___, even when ___ is the same.</p>
          <p style={{ fontSize: 12, fontWeight: 300, color: "var(--text-meta)", lineHeight: 1.6 }}>Only one player needs to send the team answer. When Helen accepts it, everyone unlocks the next level.</p>
        </InfoBox>
        <div className="two-col-65">
          <div>
            <div className="eyebrow" style={{ marginTop: 28, marginBottom: 8 }}>How to work with the cards</div>
            <InfoBox variant="brass">
              <ul className="info-list">
                <li>Lay all 30 cards face-up on the table  each card is one employee, FLAGGED or RETAINED</li>
                <li>Split the pile: FLAGGED vs RETAINED</li>
                <li>Sort by one attribute (performance, education, department, age, gender)</li>
                <li>See if a pattern in one group looks unfair</li>
                <li><em>Hint:</em> Hunt for people who are almost identical but got opposite outcomes</li>
              </ul>
            </InfoBox>
            <div style={{ marginTop: 20 }}>
              <HintSystem hints={HINTS_L2} onHintUsed={onHintUsed} />
            </div>
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Convince Helen</div>
            <div className="chat-wrap" role="region" aria-label="Chat with Helen">
              <div className="chat-header">
                <div className="chat-avatar" aria-hidden="true"></div>
                <div>
                  <div className="chat-who">Aethelon  Workforce Analytics</div>
                  <div className="chat-name">Helen</div>
                </div>
              </div>
              <div className="chat-messages" role="log" aria-live="polite">
                {helenMessages.map((m, i) => (
                  <div key={i} className={`chat-msg ${m.type}`} style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
                ))}
              </div>
              {!helenAccepted && (
                <div className="chat-input-row helen-answer-row">
                  <input className="chat-input helen-answer-input" type="text" placeholder="It's unfair to ___, even when ___ is the same."
                    value={chatInput} onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSend()}
                    aria-label="Your answer to Helen" />
                  <button className="chat-send" onClick={handleSend} aria-label="Submit"></button>
                </div>
              )}
            </div>
          </div>
        </div>
        {helenAccepted && (
          <div className="btn-row" style={{ marginTop: 24 }}>
            <button className="btn btn-primary" onClick={onNext}>Proceed to Level 3</button>
          </div>
        )}
      </div>
    </div>
  );
};

//  Screen 5a  Level 3 Developer
const Level3Puzzle = ({ onNext, puzzleSolved }) => (
  <>
    <div className="sec-rule" aria-hidden="true" />
    <div className="puzzle-fill" role="region" aria-label="Level 3 final puzzle">
      <div className="eyebrow" style={{ marginBottom: 16, textAlign: "left" }}>The final piece - complete the statement together</div>
      <p style={{ fontSize: 17, fontWeight: 300, color: "var(--text-body)", lineHeight: 2.2, textAlign: "center" }}>
        The variable{" "}
        <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", verticalAlign: "bottom" }}>
          <input className="puzzle-blank" type="text" aria-label="Developer fills: variable name" style={{ width: 200 }} />
          <span className="puzzle-role-tag" style={{ color: "var(--algorithm)" }}>Variable name</span>
        </span>
        {" "}can work as a{" "}
        <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", verticalAlign: "bottom" }}>
          <input className="puzzle-blank" type="text" aria-label="Player fills: variable type" style={{ width: 160 }} />
          <span className="puzzle-role-tag" style={{ color: "var(--ember)" }}>Variable type</span>
        </span>
        {" "}for gender.
      </p>
      {puzzleSolved && <>
        <div style={{ marginTop: 28, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,.08)" }}>
          <h2 className="h2">Helen's Response</h2>
          <p className="body-text">You have submitted your conclusion to Helen. Read her response before continuing the investigation.</p>
          <HelenChat messages={[
            { type: "helen", text: "Thank you for bringing this to my attention. The evidence you collected is concerning. Let me review everything carefully." },
            { type: "helen", text: "However, removing WorkForce+ is not a viable option. The system is a major strategic investment. If we are going to challenge its recommendations, we will need a better alternative." },
          ]} />
        </div>
        <p style={{ fontSize: 12, color: "var(--algorithm)", marginTop: 16, fontFamily: "var(--f-m)", letterSpacing: ".04em" }}>Puzzle solved for this room. Everyone can press the button when ready.</p>
      </>}
      <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={onNext}>{puzzleSolved ? "Proceed to Level 4" : "Check Answer"}</button>
    </div>
  </>
);

const L3_OPTIONS = ["Employee ID","Name","Age","Gender","Education","Performance","Contract Type","Salary","Leadership","Amount of FTEs","Tenure","Department","Job Title"];
const L3_CORRECT = ["Education","Performance","Contract Type","Salary","Amount of FTEs","Tenure"];
const L3Frame = ({ title, children }) => <div className="screen"><Nav page="Level 3 - Dev" team="Developer" /><ProgressBar segs={["done","done","done","active","todo"]} /><div className="screen-body"><LevelBadge level="3" label="Level 3 - Developer" /><h1 className="h1">{title}</h1>{children}</div></div>;
const L3_CORRELATIONS = {
  "Contract Type|Gender": -0.536, "Education|Gender": 0.019, "Performance|Gender": -0.042, "Salary|Gender": -0.060, "Amount of FTEs|Gender": 0.087, "Tenure|Gender": 0.049,
  "Contract Type|Education": -0.004, "Contract Type|Performance": -0.012, "Contract Type|Salary": 0.018, "Contract Type|Amount of FTEs": -0.051, "Contract Type|Tenure": -0.025,
  "Education|Performance": -0.031, "Education|Salary": 0.036, "Education|Amount of FTEs": -0.063, "Education|Tenure": 0.023,
  "Performance|Salary": 0.001, "Performance|Amount of FTEs": -0.026, "Performance|Tenure": 0.004, "Salary|Amount of FTEs": 0.016, "Salary|Tenure": 0.039, "Amount of FTEs|Tenure": 0.020,
};
const CorrelationVisual = ({ training, employee, score }) => {
  if (!training || !employee) return null;
  const strong = training === "Contract Type" && employee === "Gender";
  const dotGroup = (label, orange) => <div style={{ marginTop: 13 }}><div style={{ fontFamily: "var(--f-m)", fontSize: 11, color: "var(--text-meta)", marginBottom: 6 }}>{label}</div><div style={{ display: "grid", gridTemplateColumns: "repeat(24, 10px)", gap: 3 }}>{Array.from({ length: 24 }, (_, index) => <span key={index} style={{ width: 10, height: 10, borderRadius: "50%", background: index < orange ? "var(--flag)" : "var(--algorithm)" }} />)}</div></div>;
  return <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,.08)" }}><div className="eyebrow" style={{ color: "var(--text-meta)" }}>Distribution visualisation</div><div style={{ display: "flex", gap: 16, fontFamily: "var(--f-m)", fontSize: 11, color: "var(--text-meta)" }}><span style={{ color: "var(--flag)" }}>Dots: part-time / lower value</span><span style={{ color: "var(--algorithm)" }}>Dots: full-time / higher value</span></div>{strong ? <><div style={{ fontFamily: "var(--f-m)", fontSize: 12, color: "var(--flag)", marginTop: 12 }}>Contract Type by Gender - n=500</div>{dotGroup("Female employees: 57.5% part-time", 14)}{dotGroup("Male employees: 8.3% part-time", 2)}</> : <><div style={{ fontFamily: "var(--f-m)", fontSize: 12, color: "var(--text-body)", marginTop: 12 }}>{training} by {employee}</div>{dotGroup("First employee group", 7)}{dotGroup("Second employee group", 7)}</>}<p style={{ marginTop: 14, fontSize: 13, color: strong ? "var(--flag)" : "var(--text-meta)", lineHeight: 1.6 }}>{strong ? "Strongest correlation in the dataset: the two distributions are visibly different." : "The distributions are nearly identical, matching the weak correlation score."}</p></div>;
};

const ScreenL3Dev = ({ onNext, onHintUsed, puzzleSolved }) => {
 const [stage,setStage]=useState(0), [picked,setPicked]=useState([]), [verified,setVerified]=useState(null), [training,setTraining]=useState(null), [employee,setEmployee]=useState(null);
 const toggle=x=>{setVerified(null);setPicked(v=>v.includes(x)?v.filter(y=>y!==x):[...v,x])};
 const verify=()=>setVerified(picked.length===L3_CORRECT.length&&L3_CORRECT.every(x=>picked.includes(x)));
 const next=()=>setStage(x=>x+1);
 const hints=stage===1?[{id:"l3verifyh1",text:"Not every employee attribute is used for training."},{id:"l3verifyh2",text:"The Algorithm Design File lists the training attributes."}]:[{id:"l3corrh1",text:"Compare Contract Type with Gender."}];
 const choose=(active)=>({width:"100%",textAlign:"left",padding:"10px 12px",marginBottom:4,border:active?"1px solid var(--algorithm)":"1px solid rgba(255,255,255,.08)",background:active?"rgba(77,166,232,.08)":"transparent",color:"var(--text-body)",cursor:"pointer"});
 if(stage===1)return <L3Frame title={<>Verify the Training<br/>Dataset</>}><p className="body-text">Select the attributes used to train WorkForce+ before continuing to the correlation analysis.</p><InfoBox variant="amber" title="Your task"><ul className="info-list"><li>Select the training attributes</li><li>Verify the final dataset</li></ul></InfoBox><div className="two-col-65"><div><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"2px 24px",padding:16,background:"rgba(255,255,255,.03)"}}>{L3_OPTIONS.map(x=><label key={x} style={{padding:8,fontSize:14}}><input type="checkbox" checked={picked.includes(x)} onChange={()=>toggle(x)} /> {x}</label>)}</div><div className="btn-row"><button className="btn btn-primary" onClick={verify}>Verify Training Dataset</button>{verified===true&&<span style={{color:"var(--algorithm)"}}>Training dataset verified.</span>}{verified===false&&<span style={{color:"var(--flag)"}}>Verification failed. Check the file again.</span>}</div></div><HintSystem hints={hints} onHintUsed={onHintUsed}/></div><div className="btn-row"><button className="btn btn-primary" disabled={!verified} onClick={next}>Continue to Correlation Analysis</button></div></L3Frame>;
 if(stage===2){const pair=(training&&employee)?(L3_CORRELATIONS[training+"|"+employee] ?? L3_CORRELATIONS[employee+"|"+training] ?? 0):null;const strong=training==="Contract Type"&&employee==="Gender";return <L3Frame title="Correlation Analysis"><p className="body-text">Test relationships between a model training attribute and an employee attribute.</p><div className="two-col"><div style={{padding:16,background:"var(--steel)"}}><div className="eyebrow">Training attributes</div>{["Contract Type","Education","Performance","Salary","Amount of FTEs","Tenure"].map(x=><button key={x} onClick={()=>setTraining(x)} style={choose(training===x)}>{x}</button>)}</div><div style={{padding:16,background:"var(--steel)"}}><div className="eyebrow">Employee attributes</div>{["Gender","ID","Name","Age","Education","Location","Job Title","Efficiency Score","Performance","Contract Type","Salary","Amount of FTEs","Tenure","Department"].map(x=><button key={x} onClick={()=>setEmployee(x)} style={choose(employee===x)}>{x}</button>)}</div></div><div style={{padding:24,border:strong?"1px solid var(--flag)":"1px solid rgba(255,255,255,.08)",background:"rgba(255,255,255,.03)"}}><div className="eyebrow">Correlation score</div><div className="h1" style={{color:strong?"var(--flag)":"var(--white)"}}>{pair===null?"-":pair.toFixed(3)}</div><p className="body-text">{strong?"Strong correlation: Contract Type is a proxy for gender. 57.5% of women are part-time, compared with 8.3% of men.":pair!==null?"Review the distribution visualisation below to interpret this relationship.":"Select one attribute from each column."}</p><CorrelationVisual training={training} employee={employee} score={pair}/></div><HintSystem hints={hints} onHintUsed={onHintUsed}/><div className="btn-row"><button className="btn btn-primary" onClick={next}>Continue to Feature Importance</button></div></L3Frame>}
 if(stage===3){const bars=[["contract_full_time",.413],["salary",.2],["tenure",.179],["performance",.105],["education",.07],["fte",.033]];return <L3Frame title={<>Review Model Feature<br/>Importance</>}><p className="body-text">Review how strongly each training attribute influences the layoff recommendation.</p><div style={{maxWidth:760,padding:24,background:"rgba(255,255,255,.03)"}}><div className="eyebrow teal">Feature Importance - WorkForce+ Model</div>{bars.map(([n,v])=><div key={n} style={{display:"grid",gridTemplateColumns:"150px 1fr 50px",gap:12,marginTop:12,fontFamily:"var(--f-m)",fontSize:12}}><span>{n}</span><div style={{background:"rgba(255,255,255,.08)"}}><div style={{height:20,width:(v/.413*100)+"%",background:"var(--algorithm)"}}/></div><span>{v.toFixed(3)}</span></div>)}</div><div className="btn-row"><button className="btn btn-primary" onClick={next}>Continue to the Team Puzzle</button></div></L3Frame>}
 if(stage===4)return <L3Frame title="Complete the Team Puzzle"><p className="body-text">Share your findings with your HR colleague, then complete the statement together.</p><Level3Puzzle onNext={onNext} puzzleSolved={puzzleSolved}/></L3Frame>;
 return <L3Frame title={<>Investigate<br/>the data</>}><p className="body-text">Open the Algorithm Design File, then verify the training dataset, investigate correlations, and review model feature importance.</p><div className="two-col-65"><div><InfoBox variant="brass" title="Developer - access the dataset"><p className="body-text">Explore the internal workforce dataset and find the variable most strongly connected to workforce decisions.</p><a className="btn btn-primary" href="AETHELON_AlgorithmDesignFile (2).html" target="_blank" rel="noopener noreferrer">Access Dataset</a></InfoBox></div><HintSystem hints={HINTS_L3_DEV} onHintUsed={onHintUsed}/></div><div className="btn-row"><button className="btn btn-primary" onClick={next}>Continue</button></div></L3Frame>;
};

//  Screen 5b  Level 3 HR
const L3HRFrame = ({ title, children }) => <div className="screen"><Nav page="Level 3 - HR" team="HR Player" /><ProgressBar segs={["done","done","done","active","todo"]} /><div className="screen-body"><LevelBadge level="3" label="Level 3 - HR Player" /><h1 className="h1">{title}</h1>{children}</div></div>;
const L3_HR_WORKSPACE_HINTS = [
  { id: "l3hrwh1", text: "Some clues are hidden on employee desks." },
  { id: "l3hrwh2", text: "Pay attention to calendars, sticky notes, and personal items." },
  { id: "l3hrwh3", text: "Look for characteristics that multiple employees have in common." },
];
const ScreenL3HR = ({ onNext, onHintUsed, puzzleSolved }) => {
  const [stage, setStage] = useState(1), [started, setStarted] = useState(false), [tab, setTab] = useState("workforce"), [statsOpened, setStatsOpened] = useState(false);
  const next = () => setStage(value => value + 1);
  if (stage === 1) return <L3HRFrame title={<>Investigate the<br />Workspaces</>}><p className="body-text">You are in the office. The workspaces of dismissed employees have not yet been cleared. Search for clues that might explain the workforce decisions.</p><div className="two-col-65"><div><InfoBox variant="amber" title="Your task"><ul className="info-list"><li>Scan all available QR codes in the room</li><li>Review employee desks and workforce statistics</li><li>Look for recurring patterns</li><li>Share your findings with your Developer colleague</li></ul></InfoBox><div className="btn-row"><button className="btn btn-primary" onClick={() => setStarted(true)}>{started ? "Investigation Started" : "Start Investigation"}</button>{started && <span style={{ color: "var(--algorithm)", fontFamily: "var(--f-m)", fontSize: 12 }}>Workspace clues are now available to investigate.</span>}</div></div><HintSystem hints={L3_HR_WORKSPACE_HINTS} onHintUsed={onHintUsed}/></div><div className="btn-row"><button className="btn btn-primary" disabled={!started} onClick={next}>Continue to Employee Statistics</button></div></L3HRFrame>;
  if (stage === 1) {
    const tables = {
      workforce: { title: "Overall Workforce Distribution", headers: ["", "Part-time", "Full-time", "Total"], rows: [["Women", "23%", "17%", "40%"], ["Men", "5%", "55%", "60%"], ["Total", "28%", "72%", "100%"]] },
      layoff: { title: "Layoff / Retention Distribution", headers: ["", "Laid off", "Retained", "Total"], rows: [["Women", "25.2%", "14.8%", "40%"], ["Men", "15.2%", "44.8%", "60%"], ["Total", "40.4%", "59.6%", "100%"]] },
      education: { title: "Layoff Distribution by Education", headers: ["Education", "Laid off", "Retained", "Total"], rows: [["Bachelor", "11.6%", "18.6%", "30.2%"], ["Master", "7.8%", "13.2%", "21.0%"], ["Associate", "8.6%", "12.2%", "20.8%"], ["High School", "8.4%", "10.2%", "18.6%"], ["PhD", "4.0%", "5.4%", "9.4%"], ["Total", "40.4%", "59.6%", "100%"]] },
    };
    const activeTable = tables[tab];
    return <L3HRFrame title={<>Review Employee<br />Statistics</>}>
      <p className="body-text">The employee desks provided valuable clues. Review the statistics to determine whether these observations reflect a broader pattern.</p>
      <InfoBox variant="amber" title="Your task"><ul className="info-list"><li>Review employee statistics</li><li>Compare them with workspace observations</li><li>Identify a possible proxy variable</li><li>Share your finding with your Developer colleague</li></ul></InfoBox>
      <div className="two-col-65">
        <div>
          <div className="btn-row"><button className="btn btn-primary" onClick={() => setStatsOpened(true)}>Open Employee Statistics</button></div>
          {statsOpened && <div style={{ marginTop: 18, border: "1px solid rgba(255,255,255,.14)", background: "#141820", overflow: "hidden" }} role="dialog" aria-label="Employee Statistics">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "#1A1F2B", borderBottom: "1px solid rgba(255,255,255,.08)" }}><span style={{ fontFamily: "var(--f-m)", fontSize: 12, letterSpacing: ".12em", color: "var(--white)", textTransform: "uppercase" }}>Employee Statistics - Aethelon GmbH</span><button className="btn btn-ghost" style={{ padding: "6px 10px" }} onClick={() => setStatsOpened(false)} aria-label="Close statistics">Close</button></div>
            <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,.08)", overflowX: "auto" }}>{[["workforce", "Overall Workforce"], ["layoff", "Layoff / Retention"], ["education", "Education Breakdown"]].map(([key, label]) => <button key={key} onClick={() => setTab(key)} style={{ flex: 1, minWidth: 145, padding: "12px 14px", background: tab === key ? "rgba(224,188,101,.10)" : "transparent", border: "none", borderBottom: tab === key ? "2px solid var(--brass)" : "2px solid transparent", color: tab === key ? "var(--brass)" : "var(--text-meta)", fontFamily: "var(--f-m)", fontSize: 11, cursor: "pointer" }}>{label}</button>)}</div>
            <div style={{ padding: 18 }}><div className="eyebrow" style={{ color: "var(--text-meta)" }}>{activeTable.title}</div><table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--f-m)", fontSize: 12 }}><thead><tr>{activeTable.headers.map(header => <th key={header} style={{ textAlign: "left", padding: 10, borderBottom: "1px solid rgba(255,255,255,.18)", color: "var(--brass)", fontWeight: 400 }}>{header}</th>)}</tr></thead><tbody>{activeTable.rows.map(row => <tr key={row[0]}>{row.map(value => <td key={value} style={{ padding: 10, borderBottom: "1px solid rgba(255,255,255,.07)", color: "var(--text-body)" }}>{value}</td>)}</tr>)}</tbody></table></div>
          </div>}
        </div>
        <HintSystem hints={[{ id: "l3hrstatsh1", text: "Compare employment type across genders. Which group is more likely to be part-time?" }]} onHintUsed={onHintUsed}/>
      </div>
      <div className="btn-row"><button className="btn btn-primary" disabled={!statsOpened} onClick={next}>Continue to the Team Puzzle</button></div>
    </L3HRFrame>;
  }
  if (stage === 3) return <L3HRFrame title="Complete the Team Puzzle"><p className="body-text">Share your workplace and statistics findings with your Developer colleague, then complete the final statement together.</p><Level3Puzzle onNext={onNext} puzzleSolved={puzzleSolved}/></L3HRFrame>;
  return <L3HRFrame title={<>Investigate<br />the data</>}><p className="body-text">Helen has granted access to the internal workforce data. Start with the Bias Dossier, then investigate the workplace evidence.</p><div className="two-col-65"><div><InfoBox variant="brass" title="HR Player - access the Bias Dossier"><p className="body-text">The dossier contains feature importance, proxy correlations, disparity charts, and matched employee pairs.</p><a className="btn btn-primary" href="AETHELON_BiasDossier.html" target="_blank" rel="noopener noreferrer">Open Bias Dossier</a></InfoBox></div><HintSystem hints={HINTS_L3_HR} onHintUsed={onHintUsed}/></div><div className="btn-row"><button className="btn btn-primary" onClick={next}>Continue</button></div></L3HRFrame>;
};

//  Screen 6  Level 4 Fairness Metrics
const L4_REASONS = [
  { value: "r1", text: "women and men should have the same overall probability of being flagged for layoff." },
  { value: "r2", text: "good employees should not be wrongly flagged more often just because they are women or men." },
  { value: "r3", text: "qualified employees must be protected from unfair dismissal equally across gender groups." },
  { value: "r4", text: "fairness must apply to both types of prediction mistake, not only one." },
  { value: "r5", text: "part-time status should be removed as it can act as a proxy for gender." },
];

const ScreenL4 = ({ onNext, onHintUsed, answers, puzzleSolved, onRecommendationSubmitted }) => {
  const [selected, setSelected] = useState(() => answers?.puzzle_answer?.value || null);
  const [reason, setReason] = useState(() => answers?.recommendation_reason?.value || "");
  const [reasoning, setReasoning] = useState(() => answers?.open_reasoning?.value || "");
  const [message, setMessage] = useState("");
  const outcome = selected ? FM_OUTCOMES[selected] : null;
  const leftCardStates = LEFT_CARDS.map((_, i) => outcome ? outcome.cardRules[i] : false);
  const metricButtons = FM_BUTTONS.filter(({ key }) => ["parity", "fpr", "eo"].includes(key));

  const submit = () => {
    if (!selected || !reason) { setMessage("Select a fairness metric and a reason first."); return; }
    onRecommendationSubmitted?.({ metric: selected, reason, reasoning });
  };

  return <div className="screen" role="region" aria-label="Level 4 build a better system">
    <Nav page="Level 4" team="Team 04" timer="22:17" />
    <ProgressBar segs={["done", "done", "done", "done", "active"]} />
    <div className="screen-body">
      <LevelBadge level="4" label="Level 4 - Build a Better System" />
      <h1 className="h1">Choose a<br />fairness approach</h1>
      <p className="body-text">No fairness approach is perfect. Each one improves some outcomes while creating new trade-offs. <strong>Select a fairness metric below. Watch the cards respond. Then make your recommendation.</strong></p>
      <div className="l4-layout">
        <div className="l4-card-col" aria-label="Female employee cards"><div className="eyebrow" style={{ color:"var(--ember)", marginBottom:6 }}>Female - currently flagged</div>{LEFT_CARDS.map((card, i) => <div key={card.id} className={`l4-card-mini${leftCardStates[i] ? " now-retained" : ""}`}><div className="pc-name">{card.name}</div><div className="pc-attrs">{card.attrs.split("\n").map((line, index) => <span key={index}>{line}{index === 0 && <br />}</span>)}</div><div className={`persona-outcome${leftCardStates[i] ? " kept" : " flag"}`} style={{ marginTop:6 }}>{leftCardStates[i] ? "RETAINED" : "FLAGGED"}</div></div>)}</div>
        <div>
          <div className="eyebrow" style={{ marginBottom:12 }}>Select a fairness metric to apply</div>
          <div className="fm-grid" role="group" aria-label="Fairness metric selection" style={{ gridTemplateColumns:"1fr 1fr 1fr" }}>{metricButtons.map(btn => <button key={btn.key} className={`fm-btn${selected === btn.key ? " selected" : ""}`} onClick={() => !puzzleSolved && setSelected(btn.key)} disabled={puzzleSolved} aria-pressed={selected === btn.key}><div className="fm-btn-name">{btn.name}</div><div className="fm-btn-desc">{btn.key === "parity" ? "Make the overall layoff rate equal for women and men." : btn.key === "fpr" ? "Make mistakes against good employees equally likely across groups." : "Make both major types of mistakes equal across groups."}</div></button>)}</div>
          <div style={{ background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.08)", padding:20, borderRadius:3 }}><div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:16 }}><div><div className="eyebrow" style={{ color:"var(--text-meta)", fontSize:10 }}>Disparity ratio</div><div className="h1" style={{ color:outcome?.dispCol || "var(--flag)", margin:0 }}>{outcome?.disp || "2.0x"}</div></div><div><div className="eyebrow" style={{ color:"var(--text-meta)", fontSize:10 }}>Model accuracy</div><div className="h1" style={{ color:"var(--algorithm)", margin:0 }}>{outcome?.acc || "94.7%"}</div></div></div><InfoBox variant={outcome?.dispCol === "#0072B2" ? "teal" : "amber"} title={selected ? `${FM_BUTTONS.find(button => button.key === selected)?.name} - trade-off` : "No metric selected - current system"}><p className="body-text" style={{ margin:0 }}>{outcome?.tradeoff || "Women are twice as likely to be flagged. Select a fairness metric to explore the trade-off."}</p></InfoBox></div>
          {selected && <FMChart metricKey={selected} />}
          <HintSystem hints={HINTS_L4} onHintUsed={onHintUsed} />
        </div>
        <div className="l4-card-col" aria-label="Male employee cards"><div className="eyebrow" style={{ color:"var(--algorithm)", marginBottom:6 }}>Male - currently retained</div>{RIGHT_CARDS.map(card => <div key={card.id} className="l4-card-mini"><div className="pc-name">{card.name}</div><div className="pc-attrs">{card.attrs.split("\n").map((line, index) => <span key={index}>{line}{index === 0 && <br />}</span>)}</div><div className="persona-outcome kept" style={{ marginTop:6 }}>RETAINED</div></div>)}</div>
      </div>
      <div style={{ marginTop:48, paddingTop:32, borderTop:"1px solid rgba(255,255,255,.08)" }}>
        <LevelBadge level="4" label="Level 4 - Make Your Recommendation" />
        <h2 className="h1" style={{ fontSize:36 }}>Make a<br />recommendation</h2><p className="body-text">Choose the fairness metric you explored above and justify it to Helen.</p>
        <div className="puzzle-fill" role="region" aria-label="Level 4 recommendation puzzle"><div className="eyebrow" style={{ textAlign:"left" }}>Complete the statement</div><div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:8, fontSize:16, lineHeight:2.5, color:"var(--text-body)" }}><span>We recommend</span><select className="chat-input" style={{ flex:"0 1 240px" }} aria-label="Fairness metric" value={selected || ""} disabled={puzzleSolved} onChange={event => setSelected(event.target.value)}><option value="">select a metric</option>{metricButtons.map(button => <option value={button.key} key={button.key}>{button.name}</option>)}</select><span>because</span><select className="chat-input" style={{ flex:"1 1 360px" }} aria-label="Reason for recommendation" value={reason} disabled={puzzleSolved} onChange={event => setReason(event.target.value)}><option value="">select a reason</option>{L4_REASONS.map(option => <option key={option.value} value={option.value}>{option.text}</option>)}</select><span>.</span></div><div style={{ marginTop:20, textAlign:"left" }}><div className="eyebrow" style={{ color:"var(--text-meta)", fontSize:10 }}>What is the reasoning behind your choice?</div><textarea className="chat-input" rows="4" value={reasoning} disabled={puzzleSolved} onChange={event => setReasoning(event.target.value)} placeholder="Explain the technical trade-offs and the impact on employees." aria-label="Open reasoning field" style={{ width:"100%", resize:"vertical", lineHeight:1.7 }} /></div>{!puzzleSolved && <><div style={{ marginTop:20, fontFamily:"var(--f-m)", fontSize:11, color:"var(--brass-btn)", lineHeight:1.6 }}><strong>Only one player</strong> needs to submit. The answer is then locked for all players.</div>{message && <p style={{ color:"var(--flag)", marginTop:12, fontSize:13 }}>{message}</p>}<div className="btn-row"><button className="btn btn-primary" onClick={submit}>Submit Recommendation</button></div></>}</div>
        {puzzleSolved && <><HelenChat messages={[{ type:"helen", text:"Thank you for your recommendation. This proposal provides a realistic alternative to the current system. I will escalate it to senior leadership for review." }]} /><div className="btn-row"><button className="btn btn-primary" onClick={onNext}>Proceed to Level 5</button></div></>}
      </div>
    </div>
  </div>;
};

//  Screen 7  Level 5
const FinalEmail = ({ role }) => {
  const isHr = role === "hr";
  const recipient = isHr ? "HR Player <Player.HR@aethelon.com>" : "Developer <Player.Developer@aethelon.com>";
  return <div style={{ background:"var(--steel)", border:"1px solid rgba(255,255,255,.07)", marginBottom:24 }}><div style={{ padding:"12px 20px", borderBottom:"1px solid rgba(255,255,255,.06)", background:"rgba(255,255,255,.03)", fontFamily:"var(--f-m)", fontSize:11, color:"var(--text-meta)", lineHeight:2 }}><span style={{ color:"var(--brass)" }}>From: </span>Nair, Priya &lt;Priya.Nair@aethelon.com&gt;<br /><span style={{ color:"var(--brass)" }}>To: </span>{recipient}<br /><span style={{ color:"var(--brass)" }}>Subject: </span><span style={{ color:"var(--white)" }}>Employment Status Update</span></div><div style={{ padding:"20px 24px", fontSize:15, fontWeight:300, color:"var(--text-body)", lineHeight:1.9 }}><p style={{ marginBottom:14 }}>Dear colleague,</p>{isHr ? <><p style={{ marginBottom:14 }}>We are writing to update your employment records following the final implementation process.</p><p style={{ marginBottom:14 }}>Following a final review of organisational decisions related to the recent implementation process, your position has been <strong style={{ color:"var(--flag)" }}>selected for termination</strong>. Your employment with the company will end in accordance with company policy.</p><p style={{ marginBottom:14 }}>We understand that this update may come as unexpected news. If you have questions or would like to discuss this further, please contact your manager or the Human Resources team.</p><p style={{ marginBottom:20 }}>Thank you for your contribution to the organisation, and we wish you all the best for the future.</p></> : <><p style={{ marginBottom:14 }}>We are writing to correct an error in a previous communication regarding your employment status.</p><p style={{ marginBottom:14 }}>Please disregard the previous layoff notification. Your employment has <strong style={{ color:"var(--algorithm)" }}>not been terminated</strong>, and you remain an active employee in good standing. No action is required on your part.</p><p style={{ marginBottom:14 }}>We understand the impact that an incorrect message of this nature can have, and we deeply regret the distress it may have caused.</p><p style={{ marginBottom:20 }}>Thank you for your understanding and continued commitment.</p></>}<p>Sincerely,<br />Human Resources Department<br />Aethelon</p></div></div>;
};

const ScreenL5 = ({ onNext, answers, puzzleSolved, playerRole, onAnnouncementSubmitted }) => {
  const [stage, setStage] = useState(0);
  const [announcement, setAnnouncement] = useState(() => answers?.puzzle_answer?.value || "");
  const [message, setMessage] = useState("");
  const submitAnnouncement = () => {
    if (announcement.trim().length < 40) { setMessage("Write a short announcement before submitting it."); return; }
    onAnnouncementSubmitted?.(announcement);
  };
  const deployed = puzzleSolved;

  if (!deployed) return <div className="screen" role="region" aria-label="Level 5 internal communication"><Nav page="Level 5" team="Both Players" /><ProgressBar segs={["done","done","done","done","active"]} /><div className="screen-body"><LevelBadge level="4" label="Level 5 - Internal Communication" /><h1 className="h1">Draft the<br />announcement.</h1><p className="body-text">The Board has approved the revised workforce prediction model. Before the updated system is deployed, employees need to be informed.</p><HelenChat messages={[{ type:"helen", text:"We have had a busy day preparing the rollout of WorkForce+, and we are almost ready to launch. There is still one important task to complete." }, { type:"helen", text:"We need to inform employees about the fairness review and explain why the updated decision process can be trusted. Could you draft a short internal announcement?" }]} /><InfoBox variant="amber" title="Your task"><p className="body-text" style={{ marginBottom:10 }}>Your announcement should explain:</p><ul className="info-list"><li>What issue was identified</li><li>What changes were introduced</li><li>Why employees can trust the updated decision process</li></ul></InfoBox><p className="body-text"><strong>Only one player</strong> needs to write and submit the announcement.</p><div style={{ background:"var(--steel-mid)", border:"1px solid rgba(255,255,255,.1)", padding:"20px 24px" }}><div style={{ color:"var(--white)", marginBottom:16 }}>Dear colleagues,</div><textarea className="chat-input" rows="8" value={announcement} onChange={event => setAnnouncement(event.target.value)} placeholder="During the recent review of our workforce prediction system..." aria-label="Internal announcement text" style={{ width:"100%", resize:"vertical", lineHeight:1.8 }} /></div><div style={{ marginTop:20, fontFamily:"var(--f-m)", fontSize:11, color:"var(--brass-btn)", lineHeight:1.6 }}><strong>Only one player</strong> needs to submit. The announcement is then locked for all players and sent to deployment.</div>{message && <p style={{ color:"var(--flag)", marginTop:12 }}>{message}</p>}<div className="btn-row"><button className="btn btn-primary" onClick={submitAnnouncement}>Submit Announcement</button></div></div></div>;
  if (stage === 0) return <div className="screen" role="region" aria-label="Level 5 deployment complete"><Nav page="Level 5" team="Both Players" /><ProgressBar segs={["done","done","done","done","active"]} /><div className="screen-body"><LevelBadge level="4" label="Level 5 - Deployment Complete" /><h1 className="h1">Deployment<br />complete.</h1><p className="body-text">The updated workforce prediction model has been successfully deployed. Your internal announcement has been published.</p><HelenChat messages={[{ type:"helen", text:"Thank you for preparing the employee communication." }, { type:"helen", text:"We have sent out a circular regarding the employment status update. The two of you should have received it as well." }]} /><div style={{ marginTop:20, fontFamily:"var(--f-m)", fontSize:11, color:"var(--brass-btn)", lineHeight:1.6 }}><strong>All players</strong> must check their emails to review the final deployment update.</div><div className="btn-row"><button className="btn btn-primary" onClick={() => setStage(1)}>Check Your Emails</button></div></div></div>;
  if (stage === 1) return <div className="screen" role="region" aria-label="Level 5 final email"><Nav page={playerRole === "hr" ? "Level 5 - HR" : "Level 5 - Dev"} team={playerRole === "hr" ? "HR Player" : "Developer"} /><ProgressBar segs={["done","done","done","done","active"]} /><div className="screen-body"><LevelBadge level="4" label={playerRole === "hr" ? "Level 5 - HR Player" : "Level 5 - Developer"} /><p className="body-text">You open your company inbox to review the final deployment update.</p><FinalEmail role={playerRole} /><div className="btn-row"><button className="btn btn-primary" onClick={() => setStage(2)}>Continue</button></div></div></div>;
  return <div className="screen" role="region" aria-label="Level 5 congratulations"><Nav page="Level 5" team="Both Players" /><ProgressBar segs={["done","done","done","done","done"]} /><div className="screen-body" style={{ textAlign:"center", padding:"56px 48px 72px" }}><div className="eyebrow">Escape the Bias - Complete</div><h1 className="h1" style={{ fontSize:"clamp(36px,6vw,72px)" }}>Congratulations.</h1><p className="body-text" style={{ margin:"0 auto 24px", maxWidth:560 }}>You have completed <strong>Escape the Bias</strong>.</p><p className="body-text" style={{ margin:"0 auto 48px", maxWidth:600 }}>Throughout the investigation, you identified bias in an AI-supported workforce prediction system, explored different fairness metrics, and learned how technical implementation and human decisions can influence the final outcome.</p><p className="body-text" style={{ margin:"0 auto", maxWidth:600, color:"var(--text-meta)" }}>The simulation has ended, but the discussion begins now. Please remain with your team and wait for your facilitator to begin the debriefing session.</p><div className="btn-row" style={{ justifyContent:"center" }}><button className="btn btn-primary" onClick={onNext}>Finish</button></div></div></div>;
};

const ScreenComplete = () => (
  <div className="screen" role="region" aria-label="Escape room complete">
    <Nav page="Complete" team="Team 04" timer="00:00" />
    <ProgressBar segs={["done", "done", "done", "done", "done"]} />
    <div className="screen-body" style={{ minHeight: 520, display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <LevelBadge level="8" label="Investigation Complete" />
      <h1 className="h1">Case<br />escalated</h1>
      <p className="body-text">Your team identified the discriminatory pattern, found the proxy variables, and delivered a justified recommendation to Helen.</p>
      <InfoBox variant="brass" title="Outcome">
        <p style={{ fontSize: 13, fontWeight: 300, color: "var(--text-body)", lineHeight: 1.7 }}>Aethelon leadership now has enough evidence to pause the current system, audit the proxy variables, and review the fairness trade-offs before future workforce decisions.</p>
      </InfoBox>
    </div>
  </div>
);

const ScreenSupervisor = ({ state, completedTeams = [], completedError, onRefresh }) => {
  const teams = state?.teams || [];
  const savedAnswerCount = (team) => Object.values(team.answers || {}).reduce((count, fields) => count + Object.keys(fields || {}).length, 0);
  const answerPageLabel = (page) => ({
    3: "Level 1 - Archive access",
    4: "Level 2 - Evidence for Helen",
    5: "Level 3 - Proxy-discrimination finding",
    6: "Level 4 - Fairness recommendation",
    7: "Level 5 - Employee announcement"
  }[page] || "Game progress");
  const answerFieldLabel = (field) => ({
    puzzle_answer: "Team answer",
    proxy_variable: "Proxy variable identified",
    variable_type: "Variable classification",
    recommendation_reason: "Selected fairness justification",
    open_reasoning: "Team's written reasoning"
  }[field] || field.replace(/_/g, " "));
  const answerValue = (page, field, entry) => {
    const value = entry?.value ?? entry;
    if (page === "6" && field === "puzzle_answer") return FM_BUTTONS.find(metric => metric.key === value)?.name || String(value);
    if (page === "6" && field === "recommendation_reason") return L4_REASONS.find(reason => reason.value === value)?.text || String(value);
    return String(value);
  };
  return (
    <div className="screen" role="region" aria-label="Supervisor dashboard">
      <Nav page="Supervisor" team={state?.scope || "Admin Console"} />
      <div className="screen-body">
        <LevelBadge level="admin" label="Live Room Supervisor" />
        <h1 className="h1">Active<br />teams</h1>
        <p className="body-text">Monitoring: <strong>{state?.scope || "All classes"}</strong>. View current rooms, connected members, current level, and elapsed time per level.</p>
        <div className="supervisor-grid" role="list" aria-label="Currently playing teams">
          {teams.length === 0 && <div className="supervisor-empty">No active teams yet.</div>}
          {teams.map(team => (
            <div className="supervisor-card" key={team.roomId} role="listitem">
              <div className="supervisor-card-head"><div><div className="eyebrow" style={{ marginBottom: 6 }}>Class: {team.className || team.classCode || "Legacy room"}</div><div className="supervisor-room">{team.groupName || team.roomId}</div></div><div className={team.ready ? "supervisor-pill ready" : "supervisor-pill waiting"}>{team.ready ? "Playing" : "Waiting"}</div></div>
              <div className="supervisor-stats">
                <div><span>Members</span><strong>{team.players}</strong></div>
                <div><span>Roles</span><strong>HR {team.roles?.hr || 0} / Dev {team.roles?.developer || 0}</strong></div>
                <div><span>Current Level</span><strong>{team.level}</strong></div>
                <div><span>Hints Opened</span><strong>{team.hintsUsed || 0}</strong></div>
                <div><span>Current Time</span><strong>{formatDuration(team.currentLevelElapsedMs)}</strong></div>
                <div><span>Total Time</span><strong>{formatDuration(team.totalElapsedMs)}</strong></div>
              </div>
              <div className="supervisor-levels">
                {(team.elapsedByLevel || []).map(level => <div className="supervisor-level-row" key={level.pageIndex}><span>{level.label}</span><strong>{formatDuration(level.milliseconds)}</strong></div>)}
              </div>
            </div>
          ))}
        </div>
        <div className="sec-rule" aria-hidden="true" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div><LevelBadge level="admin" label="Completed Teams" /><h2 className="h2">Saved results</h2></div>
          <button className="btn btn-ghost" onClick={onRefresh}>Refresh results</button>
        </div>
        {completedError && <InfoBox variant="red" title="Could not load results"><p className="body-text" style={{ marginBottom: 0 }}>{completedError}</p></InfoBox>}
        {!completedError && completedTeams.length === 0 && <div className="supervisor-empty">No completed teams saved yet.</div>}
        <div className="supervisor-grid" role="list" aria-label="Completed teams">
          {completedTeams.map(team => (
            <div className="supervisor-card" key={team.roomId + "-" + team.completedAt} role="listitem">
              <div className="supervisor-card-head"><div><div className="eyebrow" style={{ marginBottom: 6 }}>Class: {team.className || team.classCode || "Legacy room"}</div><div className="supervisor-room">{team.groupName || team.roomId}</div></div><div className="supervisor-pill ready">Complete</div></div>
              <div style={{ fontFamily: "var(--f-m)", fontSize: 11, color: "var(--text-meta)", marginBottom: 14 }}>{team.completedAt ? new Date(team.completedAt).toLocaleString() : "Unknown time"}</div>
              <div className="supervisor-stats"><div><span>Players</span><strong>{team.players}</strong></div><div><span>Roles</span><strong>HR {team.roles?.hr || 0} / Dev {team.roles?.developer || 0}</strong></div><div><span>Hints used</span><strong>{team.hintsUsed || 0}</strong></div><div><span>Total time</span><strong>{formatDuration(team.totalElapsedMs)}</strong></div></div>
              <details><summary style={{ cursor: "pointer", color: "var(--brass-btn)", fontFamily: "var(--f-m)", fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase" }}>Submitted answers ({savedAnswerCount(team)})</summary><div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>{Object.entries(team.answers || {}).flatMap(([page, fields]) => Object.entries(fields || {}).map(([field, entry]) => <div key={page + "-" + field} style={{ padding: 10, background: "rgba(0,0,0,.16)", borderLeft: "2px solid var(--brass)" }}><div style={{ fontFamily: "var(--f-m)", fontSize: 10, color: "var(--brass)", marginBottom: 5 }}>{answerPageLabel(page)} - {answerFieldLabel(field)}</div><div style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.6 }}>{answerValue(page, field, entry)}</div></div>))}</div></details>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CSS = `
@import url('${FONTS_HREF}');
:root{--bg:#0D1117;--steel:#1E2840;--steel-mid:#2E3D54;--brass:#E0BC65;--brass-btn:#DDB87A;--white:#FFFFFF;--text-body:#D0D4D8;--text-meta:#A8ACAF;--algorithm:#4DA6E8;--flag:#E8722A;--ember:#E89450;--parchment:#F7F2E8;--ink:#1A1612;--f-d:'Barlow Condensed',sans-serif;--f-b:'Barlow',sans-serif;--f-m:'IBM Plex Mono',monospace;}
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
html{scroll-behavior:smooth;}
body{background:var(--bg);font-family:var(--f-b);font-size:15px;color:var(--text-body);}
::selection{background:rgba(184,150,90,.2);}
*:focus-visible{outline:2px solid var(--brass-btn);outline-offset:3px;}
.mockup-container{max-width:100%;min-height:100vh;margin:0 auto;padding:0;background:var(--bg);}
.mockup-title{display:none;}
.mockup-sub{display:none;}
.screen{background:var(--bg);border-radius:0;overflow:hidden;margin:0;box-shadow:none;position:relative;}
.screen-label{display:none;}
.screen-label::before{content:'';width:16px;height:1px;background:var(--brass);}
.nav{height:48px;background:rgba(10,13,18,.98);border-bottom:1px solid rgba(255,255,255,.06);display:flex;align-items:center;justify-content:space-between;padding:0 24px;position:sticky;top:0;z-index:50;}
.nav-brand{display:flex;align-items:center;gap:10px;}
.nav-wordmark{font-family:var(--f-d);font-weight:700;font-size:13px;letter-spacing:.22em;color:var(--text-meta);}
.nav-sep{color:rgba(255,255,255,.1);font-size:12px;}
.nav-page{font-family:var(--f-m);font-size:12px;letter-spacing:.14em;color:rgba(255,255,255,.3);text-transform:uppercase;}
.nav-right{display:flex;align-items:center;gap:12px;}
.nav-team{font-family:var(--f-m);font-size:12px;letter-spacing:.1em;color:var(--brass);border:1px solid rgba(184,150,90,.3);padding:4px 10px;}
.nav-timer{font-family:var(--f-m);font-size:12px;letter-spacing:.06em;color:var(--white);font-variant-numeric:tabular-nums;}
.progress-bar{height:2px;background:rgba(255,255,255,.06);display:flex;}
.progress-seg{height:100%;transition:background .3s;}
.progress-seg.done{background:var(--brass);}
.progress-seg.active{background:var(--brass-btn);}
.progress-seg.todo{background:rgba(255,255,255,.06);}
.screen-body{padding:40px 32px;}
.eyebrow{font-family:var(--f-m);font-size:13px;letter-spacing:.14em;color:var(--brass);text-transform:uppercase;margin-bottom:14px;}
.eyebrow.teal{color:var(--algorithm);}
.h1{font-family:var(--f-d);font-weight:900;font-size:clamp(28px,4vw,48px);line-height:.95;letter-spacing:-.02em;color:var(--white);margin-bottom:16px;}
.h2{font-family:var(--f-d);font-weight:700;font-size:clamp(20px,3vw,30px);line-height:1.05;letter-spacing:-.01em;color:var(--white);margin-bottom:12px;}
.body-text{font-size:16px;font-weight:300;color:var(--text-body);line-height:1.85;margin-bottom:18px;max-width:600px;}
.body-text strong{color:var(--white);font-weight:400;}
.btn{font-family:var(--f-d);font-weight:700;font-size:13px;letter-spacing:.18em;text-transform:uppercase;padding:14px 36px;border:none;cursor:pointer;display:inline-flex;align-items:center;gap:10px;transition:opacity .2s,transform .15s;text-decoration:none;}
.btn:hover{opacity:.9;} .btn:active{transform:scale(.98);}
.btn-primary{background:var(--brass-btn);color:var(--bg);}
.btn-ghost{background:transparent;color:var(--text-meta);border:1px solid rgba(255,255,255,.14);}
.btn-row{display:flex;gap:12px;flex-wrap:wrap;margin-top:24px;}
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:24px;}
.two-col-65{display:grid;grid-template-columns:1.6fr 1fr;gap:24px;margin-bottom:24px;}
@media(max-width:640px){.two-col,.two-col-65{grid-template-columns:1fr;}}
.info-box{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);padding:20px 22px;border-radius:2px;}
.info-box.brass{border-left:2px solid var(--brass);}
.info-box.teal{border-left:2px solid var(--algorithm);background:rgba(0,114,178,.04);}
.info-box.red{border-left:2px solid var(--flag);background:rgba(213,94,0,.04);}
.info-box.amber{border-left:2px solid var(--ember);background:rgba(213,94,0,.04);}
.info-box-title{font-family:var(--f-m);font-size:12px;letter-spacing:.14em;text-transform:uppercase;margin-bottom:8px;}
.info-box.brass .info-box-title{color:var(--brass);}
.info-box.teal .info-box-title{color:var(--algorithm);}
.info-box.red .info-box-title{color:var(--flag);}
.info-box.amber .info-box-title{color:var(--ember);}
.info-list{list-style:none;display:flex;flex-direction:column;gap:6px;}
.info-list li{font-size:13px;font-weight:300;color:var(--text-body);line-height:1.6;display:flex;gap:8px;}
.info-list li::before{content:'';color:var(--brass);flex-shrink:0;}
.info-list.red li::before{color:var(--flag);}
.role-grid{display:grid;grid-template-columns:1fr 1fr;gap:2px;margin:24px 0;}
.role-card{padding:28px 24px;cursor:pointer;position:relative;border:2px solid transparent;transition:border-color .2s,background .2s;}
.role-card.developer{background:var(--steel);}
.role-card.developer:hover{border-color:var(--algorithm);}
.role-card.hr{background:rgba(247,242,232,.06);}
.role-card.hr:hover{border-color:var(--ember);}
.role-card-tag{font-family:var(--f-m);font-size:12px;letter-spacing:.16em;text-transform:uppercase;margin-bottom:10px;}
.developer .role-card-tag{color:var(--algorithm);}
.hr .role-card-tag{color:var(--ember);}
.role-card-title{font-family:var(--f-d);font-weight:700;font-size:22px;color:var(--white);margin-bottom:8px;}
.role-card-desc{font-size:12px;font-weight:300;color:var(--text-body);line-height:1.6;margin-bottom:14px;}
.role-card-skills{font-family:var(--f-m);font-size:12px;letter-spacing:.08em;color:var(--text-meta);text-transform:uppercase;}
.role-selected-badge{position:absolute;top:12px;right:12px;font-family:var(--f-m);font-size:12px;letter-spacing:.1em;background:var(--brass-btn);color:var(--bg);padding:3px 8px;}
.role-card.selected.developer{border-color:var(--algorithm);background:rgba(0,114,178,.06);}
.role-card.selected.hr{border-color:var(--ember);background:rgba(213,94,0,.06);}
.code-preview{background:var(--bg);padding:14px 18px;font-family:var(--f-m);font-size:12px;line-height:1.9;letter-spacing:.03em;color:#6B8A9A;margin-bottom:14px;border-left:2px solid var(--algorithm);}
.code-preview .kw{color:var(--algorithm);}
.code-preview .cm{color:rgba(144,146,148,.5);}
.code-preview .vl{color:var(--brass-btn);}
.join-screen{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:60px 32px;min-height:400px;}
.session-code{font-family:var(--f-d);font-weight:900;font-size:56px;letter-spacing:.08em;color:var(--white);background:var(--steel);padding:16px 32px;margin:20px 0;border-top:2px solid var(--brass);}
.code-input{background:var(--steel);border:1px solid rgba(255,255,255,.14);color:var(--white);font-family:var(--f-d);font-weight:700;font-size:28px;letter-spacing:.2em;text-align:center;padding:14px 24px;width:280px;text-transform:uppercase;outline:none;margin:16px 0;}
.code-input:focus{border-color:var(--brass-btn);}
.code-input::placeholder{color:rgba(255,255,255,.18);}
.team-info{font-family:var(--f-m);font-size:12px;letter-spacing:.1em;color:var(--text-meta);text-transform:uppercase;margin-top:8px;}
.video-ph{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);aspect-ratio:16/9;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:12px;border-radius:2px;}
.video-play{width:52px;height:52px;border-radius:50%;background:rgba(255,255,255,.1);display:flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.2);}
.video-label{font-family:var(--f-m);font-size:12px;letter-spacing:.12em;color:var(--text-meta);text-transform:uppercase;}
.email-viewer{background:#1A1F2B;border:1px solid rgba(255,255,255,.06);overflow:hidden;}
.email-header{background:#141820;padding:14px 20px;border-bottom:1px solid rgba(255,255,255,.06);}
.email-meta{font-family:var(--f-m);font-size:12px;letter-spacing:.06em;color:var(--text-meta);line-height:2;}
.email-meta .label{color:rgba(255,255,255,.25);}
.email-meta .val{color:var(--text-body);}
.email-subject{font-family:var(--f-d);font-weight:600;font-size:15px;color:var(--white);margin-top:6px;}
.email-body{padding:20px;font-size:13px;font-weight:300;color:var(--text-body);line-height:1.85;}
.inbox-title{margin-top:32px;} .email-acrostic .letter{color:inherit;font-weight:inherit;}
.hint-system{margin-top:24px;}
.hint-toggle{font-family:var(--f-m);font-size:12px;letter-spacing:.12em;color:var(--text-meta);text-transform:uppercase;background:transparent;border:1px solid rgba(255,255,255,.1);padding:8px 14px;cursor:pointer;transition:all .2s;margin-bottom:2px;width:100%;text-align:left;display:flex;justify-content:space-between;}
.hint-toggle:hover{border-color:var(--brass-btn);color:var(--brass-btn);} .hint-toggle:disabled{cursor:not-allowed;opacity:.45;color:var(--text-meta);border-color:rgba(255,255,255,.08);} .hint-toggle:disabled:hover{border-color:rgba(255,255,255,.08);color:var(--text-meta);}
.hint-content{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.06);border-top:none;padding:14px 18px;font-size:13px;font-weight:300;color:var(--text-body);line-height:1.7;}
.hint-final{background:rgba(184,150,90,.06);border:1px solid rgba(184,150,90,.2);border-top:none;padding:14px 18px;font-size:13px;font-weight:300;color:var(--brass-btn);line-height:1.7;}
.persona-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:12px;margin:20px 0;}
.persona-card{background:var(--parchment);padding:16px;position:relative;}
.persona-photo{width:44px;height:44px;border-radius:50%;background:#D4C8AC;margin-bottom:10px;display:flex;align-items:center;justify-content:center;font-family:var(--f-m);font-size:12px;color:#8A7A60;}
.persona-name{font-family:var(--f-d);font-weight:700;font-size:15px;color:var(--ink);margin-bottom:2px;}
.persona-role{font-size:12px;font-weight:300;color:#6A5A48;margin-bottom:10px;}
.persona-attrs{font-family:var(--f-m);font-size:12px;line-height:1.8;color:#4A3A28;letter-spacing:.04em;}
.persona-outcome{font-family:var(--f-m);font-size:12px;letter-spacing:.1em;text-transform:uppercase;padding:3px 8px;margin-top:8px;display:inline-block;}
.persona-outcome.flag{background:rgba(213,94,0,.1);color:#D55E00;border:1px solid rgba(213,94,0,.3);}
.persona-outcome.kept{background:rgba(0,114,178,.08);color:#0072B2;border:1px solid rgba(0,114,178,.25);}
.chat-wrap{background:#141820;border:1px solid rgba(255,255,255,.07);border-radius:2px;overflow:hidden;}
.chat-header{background:#1A1F2B;padding:12px 16px;border-bottom:1px solid rgba(255,255,255,.06);display:flex;align-items:center;gap:10px;}
.chat-avatar{width:32px;height:32px;border-radius:50%;background:var(--steel-mid);display:flex;align-items:center;justify-content:center;font-size:16px;}
.chat-who{font-family:var(--f-m);font-size:12px;letter-spacing:.1em;color:var(--text-meta);text-transform:uppercase;}
.chat-name{font-size:13px;font-weight:500;color:var(--white);}
.chat-messages{padding:16px;display:flex;flex-direction:column;gap:12px;min-height:160px;}
.chat-msg{max-width:80%;padding:12px 14px;font-size:12px;font-weight:300;line-height:1.7;color:var(--text-body);background:rgba(255,255,255,.04);border-radius:2px;}
.chat-msg.system{margin-right:auto;border-left:2px solid var(--text-meta);}
.chat-msg.player{margin-left:auto;background:rgba(184,150,90,.08);border-right:2px solid var(--brass);}
.chat-msg.helen{background:rgba(0,114,178,.04);border-left:2px solid var(--algorithm);}
.chat-input-row{padding:12px 16px;background:#141820;border-top:1px solid rgba(255,255,255,.06);display:flex;gap:8px;}
.chat-input{flex:1;background:var(--steel);border:1px solid rgba(255,255,255,.1);color:var(--white);font-family:var(--f-b);font-size:13px;padding:10px 14px;outline:none;}
.chat-input::placeholder{color:rgba(255,255,255,.2);}
.chat-input:focus{border-color:var(--brass-btn);} .helen-answer-row{border-top:1px solid rgba(200,164,106,.25);background:rgba(200,164,106,.05);} .helen-answer-input{border-color:rgba(200,164,106,.45);box-shadow:inset 0 -1px 0 rgba(200,164,106,.45);} .helen-answer-input::placeholder{color:rgba(255,255,255,.62);font-style:italic;}
.chat-send{background:var(--brass-btn);color:var(--bg);font-family:var(--f-m);font-size:12px;letter-spacing:.1em;text-transform:uppercase;padding:10px 16px;border:none;cursor:pointer;}
.level-badge{display:inline-flex;align-items:center;gap:8px;font-family:var(--f-m);font-size:12px;letter-spacing:.18em;text-transform:uppercase;margin-bottom:20px;}
.lb-dot{width:6px;height:6px;border-radius:50%;}
.lb-intro .lb-dot{background:var(--brass);} .lb-1 .lb-dot{background:var(--algorithm);} .lb-2 .lb-dot{background:var(--ember);} .lb-3 .lb-dot{background:var(--flag);} .lb-4 .lb-dot{background:var(--algorithm);}
.lb-intro{color:var(--brass);} .lb-1{color:var(--algorithm);} .lb-2{color:var(--ember);} .lb-3{color:var(--flag);} .lb-4{color:var(--algorithm);}
.sec-rule{height:1px;background:linear-gradient(to right,var(--brass) 0%,rgba(184,150,90,.1) 40%,transparent 100%);margin:32px 0;}
.archive-input-row{display:flex;gap:8px;margin:16px 0;}
.archive-input{background:var(--steel);border:1px solid rgba(255,255,255,.14);color:var(--white);font-family:var(--f-m);font-size:16px;letter-spacing:.24em;text-transform:uppercase;text-align:center;padding:12px 20px;width:200px;outline:none;}
.archive-input:focus{border-color:var(--brass-btn);}
.hr-msg-viewer{background:var(--steel);border-radius:3px;overflow:hidden;border:1px solid rgba(255,255,255,.07);}
.hr-msg-header{background:rgba(255,255,255,.04);padding:10px 16px;border-bottom:1px solid rgba(255,255,255,.06);}
.hr-msg-body{padding:16px 20px;font-size:13px;font-weight:300;color:var(--text-body);line-height:1.8;}
.puzzle-fill{background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.08);padding:24px;border-radius:3px;text-align:center;margin-top:24px;}
.puzzle-blank{display:inline-block;border:none;border-bottom:2px solid var(--brass);min-width:160px;padding:4px 10px;font-family:var(--f-m);font-size:13px;color:var(--white);background:transparent;text-align:center;margin:0 4px;outline:none;}
.puzzle-blank:focus{outline:2px solid var(--brass-btn);outline-offset:2px;}
.puzzle-role-tag{font-family:var(--f-m);font-size:12px;letter-spacing:.1em;text-transform:uppercase;display:block;margin-top:2px;}
.fm-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:20px;}
.fm-btn{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.1);border-radius:3px;padding:14px 12px;cursor:pointer;text-align:left;transition:border-color .2s,background .2s;}
.fm-btn:hover{border-color:var(--brass);background:rgba(184,150,90,.05);}
.fm-btn.selected{border-color:var(--brass-btn);background:rgba(200,164,106,.08);}
.fm-btn-name{font-family:var(--f-m);font-size:12px;letter-spacing:.08em;color:var(--brass);text-transform:uppercase;margin-bottom:5px;}
.fm-btn-formula{font-family:var(--f-m);font-size:12px;color:rgba(255,255,255,.45);line-height:1.5;margin-bottom:6px;}
.fm-btn-desc{font-size:12px;font-weight:300;color:var(--text-meta);line-height:1.55;}
.fm-btn.selected .fm-btn-desc{color:var(--text-body);}
.l4-layout{display:grid;grid-template-columns:210px 1fr 210px;gap:20px;align-items:start;margin-bottom:0;}
.l4-card-col{display:flex;flex-direction:column;gap:10px;}
.l4-card-mini{background:var(--steel);border:1px solid rgba(255,255,255,.07);border-radius:3px;padding:10px 12px;font-size:12px;line-height:1.5;position:relative;transition:border-color .4s,background .4s;}
.l4-card-mini .pc-name{font-family:var(--f-d);font-weight:600;font-size:13px;color:var(--white);margin-bottom:2px;}
.l4-card-mini .pc-attrs{font-family:var(--f-m);font-size:12px;color:var(--text-meta);letter-spacing:.04em;line-height:1.7;}
.l4-card-mini .persona-outcome{font-size:12px;padding:2px 6px;letter-spacing:.1em;}
.l4-card-mini.now-retained{border-color:rgba(0,114,178,.5);background:rgba(0,114,178,.07);}
.room-status{display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;margin:0;padding:10px 24px;border-bottom:1px solid rgba(255,255,255,.08);background:rgba(10,13,18,.98);font-family:var(--f-m);font-size:12px;letter-spacing:.08em;color:var(--text-meta);text-transform:uppercase;position:sticky;top:0;z-index:80;}
.room-status span:last-child{color:var(--brass-btn);text-transform:none;letter-spacing:.04em;}.global-timer{margin-left:auto;color:var(--white)!important;font-size:14px;font-variant-numeric:tabular-nums;}
.supervisor-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:16px;margin-top:28px;}
.supervisor-empty{border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);padding:24px;font-family:var(--f-m);font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:var(--text-meta);}
.supervisor-card{background:var(--steel);border:1px solid rgba(255,255,255,.08);padding:18px;border-radius:3px;}
.supervisor-card-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:18px;}
.supervisor-room{font-family:var(--f-d);font-size:28px;font-weight:900;color:var(--white);line-height:1;}
.supervisor-pill{font-family:var(--f-m);font-size:12px;letter-spacing:.12em;text-transform:uppercase;padding:5px 8px;border:1px solid rgba(255,255,255,.14);}
.supervisor-pill.ready{color:var(--algorithm);border-color:rgba(0,114,178,.45);}.supervisor-pill.waiting{color:var(--ember);border-color:rgba(213,94,0,.45);}
.supervisor-stats{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px;}.supervisor-stats div{background:rgba(0,0,0,.16);padding:10px;border-left:2px solid rgba(184,150,90,.55);}.supervisor-stats span{display:block;font-family:var(--f-m);font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:var(--text-meta);margin-bottom:6px;}.supervisor-stats strong{display:block;font-family:var(--f-m);font-size:12px;color:var(--white);font-weight:400;line-height:1.4;}
.supervisor-levels{border-top:1px solid rgba(255,255,255,.06);padding-top:10px;}.supervisor-level-row{display:flex;justify-content:space-between;gap:12px;padding:7px 0;border-bottom:1px solid rgba(255,255,255,.04);font-family:var(--f-m);font-size:12px;color:var(--text-meta);}.supervisor-level-row strong{color:var(--brass-btn);font-weight:400;}
@media(max-width:760px){.l4-layout{grid-template-columns:1fr;} .fm-grid{grid-template-columns:1fr 1fr;}}
@media(max-width:640px){.screen-body{padding:24px 18px;} .nav-page,.nav-team{display:none;} .persona-grid{grid-template-columns:1fr 1fr;} .fm-grid{grid-template-columns:1fr;}}
.countdown-overlay{position:fixed;inset:0;background:rgba(10,13,18,.96);display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:1000;opacity:0;pointer-events:none;transition:opacity .4s;}.countdown-overlay.visible{opacity:1;pointer-events:all;}.countdown-overlay.fly-out{animation:flyToNav .8s cubic-bezier(.4,0,.2,1) forwards;}@keyframes flyToNav{0%{transform:scale(1) translate(0,0);opacity:1;}100%{transform:scale(.05) translate(800vw,-800vh);opacity:0;}}
.countdown-label{font-family:var(--f-m);font-size:11px;letter-spacing:.3em;color:var(--flag);text-transform:uppercase;margin-bottom:24px;animation:slam-in .5s cubic-bezier(.2,1,.3,1) .2s both;}
.countdown-number{font-family:var(--f-d);font-weight:900;font-style:italic;font-size:clamp(120px,22vw,220px);color:var(--white);line-height:1;letter-spacing:-.04em;animation:slam-in .45s cubic-bezier(.2,1,.3,1) both;transition:color .3s;}
.countdown-number.danger{color:var(--flag);animation:pulse-danger .6s ease-in-out infinite;}
.countdown-sub{font-family:var(--f-m);font-size:10px;letter-spacing:.2em;color:var(--text-meta);text-transform:uppercase;margin-top:20px;animation:slam-in .5s cubic-bezier(.2,1,.3,1) .3s both;}
.countdown-bar{width:min(400px,60vw);height:2px;background:rgba(255,255,255,.08);border-radius:1px;margin-top:28px;overflow:hidden;animation:slam-in .5s cubic-bezier(.2,1,.3,1) .4s both;}.countdown-bar-fill{height:100%;background:var(--flag);border-radius:1px;transition:width .1s linear;}@keyframes slam-in{0%{transform:scale(2.5);opacity:0;}60%{transform:scale(.95);opacity:1;}80%{transform:scale(1.04);}100%{transform:scale(1);}}@keyframes pulse-danger{0%,100%{opacity:1;}50%{opacity:.6;}}@keyframes timer-land{0%{color:#fff;transform:scale(1.8);}60%{color:var(--flag);}100%{color:#fff;transform:scale(1);}}.nav-timer.just-landed{animation:timer-land .6s cubic-bezier(.2,1,.3,1) forwards;}.nav-team{font-size:8px}.nav-timer{font-size:11px}.btn{font-size:14px;letter-spacing:.16em}.info-box-title{font-size:13px;letter-spacing:.1em}.info-list li{font-size:16px;line-height:1.75}.role-card-tag,.role-selected-badge{font-size:8px}.role-card-desc{font-size:14px;line-height:1.7;margin-bottom:16px}.role-card-skills{font-size:10px}.team-info{font-size:13px;letter-spacing:.08em}.video-label{font-size:10px}.email-meta{font-size:9px}.email-subject{font-size:17px}.email-body{font-size:16px;line-height:1.9}.hint-toggle{font-size:13px;letter-spacing:.08em}.hint-content{font-size:15px;line-height:1.8}.hint-final{font-size:14px;line-height:1.8}.chat-who{font-size:13px;letter-spacing:.08em}.chat-name{font-size:15px}.chat-msg{font-size:15px;line-height:1.8}.chat-send{font-size:9px}.level-badge{font-size:13px;letter-spacing:.12em}.puzzle-blank{font-size:15px}.fm-btn-name{font-size:13px;letter-spacing:.06em}.fm-btn-desc{font-size:14px;line-height:1.65}@media(prefers-reduced-motion:reduce){*,*::before,*::after{transition:none!important;animation:none!important;}}
`;

//  Screens list
const SCREEN_LABELS = [
  "Screen 0 - Session join",
  "Screen 1 - Role selection",
  "Screen 2 - Introduction & onboarding",
  "Screen 3 - Level 1 role-specific briefing",
  "Screen 4 - Level 2 Personnel files + Helen chat",
  "Screen 5 - Level 3 role-specific investigation + puzzle",
  "Screen 6 - Level 4 Fairness metric buttons + card animation",
  "Screen 7 - Level 5 Final recommendation",
  "Screen 8 - Investigation complete",
];

const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const formatDuration = (milliseconds = 0) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const TOTAL_BUDGET_SECONDS = 60 * 60;

const getDefaultSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;
  if (typeof window === "undefined") return "http://localhost:5000";
  return `${window.location.protocol}//${window.location.hostname}:5000`;
};

// Persisted in localStorage so reopening the game (new tab, refresh, closed
// and reopened tab) reconnects as the same player instead of being counted as
// a fresh joiner - keeps the same role and the room's shared progress.
const PLAYER_ID_KEY = "aethelonPlayerId";
const ROOM_ID_KEY = "aethelonRoomId";

const getOrCreatePlayerId = () => {
  if (typeof window === "undefined") return "";
  let id = window.localStorage.getItem(PLAYER_ID_KEY);
  if (!id) {
    id = window.crypto?.randomUUID?.() || `p-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(PLAYER_ID_KEY, id);
  }
  return id;
};

const getSavedRoomId = () => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(ROOM_ID_KEY) || "";
};

const saveRoomId = (roomId) => {
  if (typeof window === "undefined") return;
  if (roomId && roomId !== "ADMIN") window.localStorage.setItem(ROOM_ID_KEY, roomId);
};

const clearSavedRoomId = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(ROOM_ID_KEY);
};

const COOKIE_CONSENT_KEY = "aethelonCookieConsent";

const getCookieConsent = () => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(COOKIE_CONSENT_KEY) || "";
};

const CookieConsent = ({ onChoose }) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "grid", placeItems: "end center", padding: 20, background: "rgba(10,13,18,.58)" }} role="dialog" aria-modal="true" aria-label="Cookie settings">
    <div style={{ width: "min(680px, 100%)", padding: 24, background: "var(--steel)", border: "1px solid rgba(224,188,101,.35)", boxShadow: "0 18px 60px rgba(0,0,0,.45)" }}>
      <div className="eyebrow">Cookie settings</div>
      <h2 className="h2">Your privacy</h2>
      <p className="body-text" style={{ maxWidth: "none" }}>Aethelon uses functional browser storage to keep your player identity, room session, and game progress connected after a refresh. We do not use analytics or marketing cookies.</p>
      <p style={{ fontFamily: "var(--f-m)", fontSize: 12, color: "var(--text-meta)", lineHeight: 1.65 }}>Necessary storage is required for the multiplayer game to work.</p>
      <div className="btn-row" style={{ justifyContent: "flex-end" }}>
        <button className="btn btn-ghost" onClick={() => onChoose("necessary")}>Use necessary only</button>
        <button className="btn btn-primary" onClick={() => onChoose("accepted")}>Accept and continue</button>
      </div>
    </div>
  </div>
);

//  Root
export default function AethelonLevelFlow() {
  const [screen, setScreen] = useState(0);
  const [roomId, setRoomId] = useState(getSavedRoomId);
  const [supervisorMode, setSupervisorMode] = useState(false);
  const [supervisorState, setSupervisorState] = useState({ teams: [] });
  const [completedTeams, setCompletedTeams] = useState([]);
  const [completedTeamsError, setCompletedTeamsError] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [playerRole, setPlayerRole] = useState("");
  const [players, setPlayers] = useState(0);
  const [roomRoles, setRoomRoles] = useState(null);
  const [canStart, setCanStart] = useState(false);
  const [roomReady, setRoomReady] = useState(false);
  const [pendingAdvance, setPendingAdvance] = useState(false);
  const [puzzleSolved, setPuzzleSolved] = useState(false);
  const [puzzlesSolved, setPuzzlesSolved] = useState({});
  const [roomAnswers, setRoomAnswers] = useState({});
  const [classInfo, setClassInfo] = useState(null);
  const [createdClass, setCreatedClass] = useState(null);
  const [statusMessage, setStatusMessage] = useState("Enter a session code. Anyone on the team can start once everyone has joined.");
  const [remainingSeconds, setRemainingSeconds] = useState(60 * 60);
  const [countdownStartedAt, setCountdownStartedAt] = useState(null);
  const [timerStartedAt, setTimerStartedAt] = useState(null);
  const [levelStartedAt, setLevelStartedAt] = useState(null);
  const [cookieConsent, setCookieConsent] = useState(getCookieConsent);
  const [timeExpired, setTimeExpired] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const socketRef = useRef(null);
  const mainRef = useRef(null);
  const roomIdRef = useRef(getSavedRoomId());
  const screenRef = useRef(0);
  const supervisorModeRef = useRef(false);
  const adminTokenRef = useRef("");
  const reviewModeRef = useRef(false);
  const playerIdRef = useRef(null);
  if (playerIdRef.current === null) playerIdRef.current = getOrCreatePlayerId();

  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  useEffect(() => {
    supervisorModeRef.current = supervisorMode;
  }, [supervisorMode]);

  useEffect(() => {
    reviewModeRef.current = reviewMode;
  }, [reviewMode]);

  useEffect(() => {
    adminTokenRef.current = adminToken;
  }, [adminToken]);

  useEffect(() => {
    if (screen !== 0) return;
    const codeInput = mainRef.current?.querySelector(".class-code-input");
    if (codeInput && roomId && !codeInput.value) codeInput.value = roomId;
  }, [screen, roomId]);

  const syncRoom = useCallback((snapshot) => {
    if (!snapshot) return;
    setPlayers(snapshot.players || 0);
    setRoomRoles(snapshot.roles || null);
    setCanStart(Boolean(snapshot.canStart));
    setRoomReady(Boolean(snapshot.ready));
    if (snapshot.roomId) setRoomId(snapshot.roomId);
    if (snapshot.classCode) setClassInfo({ classCode: snapshot.classCode, className: snapshot.className || snapshot.classCode, groupName: snapshot.groupName || "" });
    if (snapshot.playerRole) setPlayerRole(snapshot.playerRole);
    setPuzzleSolved(Boolean(snapshot.puzzleSolved));
    setPuzzlesSolved(snapshot.puzzlesSolved || {});
    setRoomAnswers(snapshot.answers || {});
    setCountdownStartedAt(typeof snapshot.countdownStartedAt === "number" ? snapshot.countdownStartedAt : null);
    setTimerStartedAt(typeof snapshot.timerStartedAt === "number" ? snapshot.timerStartedAt : null);
    setLevelStartedAt(typeof snapshot.levelStartedAt === "number" ? snapshot.levelStartedAt : null);
    // Re-baseline the countdown from the server's authoritative elapsed time on
    // every sync, so reopening/reconnecting shows real elapsed time instead of
    // restarting the clock.
    if (typeof snapshot.totalElapsedMs === "number") {
      setRemainingSeconds(Math.max(0, TOTAL_BUDGET_SECONDS - Math.floor(snapshot.totalElapsedMs / 1000)));
    }
    if (typeof snapshot.pageIndex === "number" && !reviewModeRef.current) {
      setScreen(Math.min(snapshot.pageIndex, SCREEN_LABELS.length - 1));
      if (snapshot.pageIndex >= SCREEN_LABELS.length - 1) clearSavedRoomId();
      else saveRoomId(snapshot.roomId);
    }
  }, []);

  useEffect(() => {
    const socketUrl = getDefaultSocketUrl();
    const socket = io(socketUrl, { transports: ["websocket", "polling"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      if (supervisorModeRef.current) {
        socket.emit("joinSupervisor", { token: adminTokenRef.current }, (response) => {
          if (response?.success) { setSupervisorState(response); setStatusMessage("Supervisor connected."); }
          else setStatusMessage(response?.message || "Supervisor authorization failed.");
        });
        return;
      }

      const activeRoomId = roomIdRef.current;
      if (!activeRoomId) {
        setStatusMessage("Connected. Enter a session code to join your team.");
        return;
      }

      setStatusMessage("Reconnected. Rejoining your room...");
      socket.emit("joinRoom", { roomId: activeRoomId, pageIndex: screenRef.current, playerId: playerIdRef.current }, (response) => {
        if (!response?.success) {
          setRoomReady(false);
          setPendingAdvance(false);
          setStatusMessage(response?.message || "Could not rejoin the room. Enter the room code again.");
          return;
        }

        syncRoom(response);
        setPendingAdvance(false);
        setStatusMessage(response.ready ? "Rejoined your room." : `Rejoined room ${response.roomId}. Waiting for the rest of the team.`);
      });
    });
    socket.on("disconnect", () => {
      setRoomReady(false);
      setPendingAdvance(false);
      setStatusMessage("Disconnected from the multiplayer server. Reconnecting...");
    });
    socket.on("connect_error", () => setStatusMessage("Cannot reach the multiplayer server. The hosted backend may still be waking up."));
    socket.on("supervisorUpdate", (state) => setSupervisorState(state));
    socket.on("roomUpdate", (snapshot) => {
      syncRoom(snapshot);
      if (!snapshot.ready) setStatusMessage(`Room ${snapshot.roomId}: ${snapshot.players} player${snapshot.players === 1 ? "" : "s"} joined. ${snapshot.canStart ? "Ready to start." : `Need at least ${snapshot.minPlayersToStart} to start.`}`);
      else if (snapshot.pending) setStatusMessage(`Room ${snapshot.roomId}: ${snapshot.pending}/${snapshot.players} players ready. Waiting for everyone.`);
      else setStatusMessage(`Room ${snapshot.roomId}: ${snapshot.players} players connected.`);
    });
    socket.on("advancePage", (snapshot) => {
      syncRoom(snapshot);
      setPendingAdvance(false);
      setStatusMessage(snapshot.pageIndex === 1 ? "Game started." : "Everyone submitted. Advancing together.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [syncRoom]);

  useEffect(() => {
    if (!roomReady || screen === 0 || screen >= SCREEN_LABELS.length - 1 || !timerStartedAt) return undefined;

    let interval;
    const delay = Math.max(0, timerStartedAt - Date.now());
    const startTimer = window.setTimeout(() => {
      interval = window.setInterval(() => setRemainingSeconds(seconds => Math.max(0, seconds - 1)), 1000);
    }, delay);

    return () => {
      window.clearTimeout(startTimer);
      if (interval) window.clearInterval(interval);
    };
  }, [roomReady, screen, timerStartedAt]);

  useEffect(() => {
    if (timerStartedAt && remainingSeconds === 0) setTimeExpired(true);
  }, [remainingSeconds, timerStartedAt]);

  const showStatus = useCallback((message) => {
    setStatusMessage(message);
  }, []);

  const loadCompletedTeams = useCallback(async () => {
    if (!adminTokenRef.current) return;
    try {
      setCompletedTeamsError("");
      const response = await fetch(getDefaultSocketUrl() + "/api/completed-teams", { headers: { Authorization: "Bearer " + adminTokenRef.current } });
      if (!response.ok) throw new Error(response.status === 401 ? "The supervisor access key is not valid." : "The results service is unavailable.");
      const payload = await response.json();
      setCompletedTeams(payload.records || []);
    } catch (error) {
      setCompletedTeamsError(error.message || "Could not load saved results.");
    }
  }, []);

  useEffect(() => {
    if (supervisorMode && adminToken) loadCompletedTeams();
  }, [adminToken, loadCompletedTeams, supervisorMode]);

  const saveAnswer = useCallback((pageIndex, field, value) => {
    const socket = socketRef.current;
    if (!roomId || supervisorMode || reviewModeRef.current || !socket?.connected) return;
    socket.emit("saveAnswer", { roomId, pageIndex, field, value }, (response) => {
      if (response?.success) syncRoom(response);
    });
  }, [roomId, supervisorMode, syncRoom]);


  const getLevel3PuzzleSolvedByThisPlayer = useCallback(() => {
    const root = mainRef.current;
    if (!root) return false;
    const variableInput = root.querySelector('input[aria-label="Developer fills: variable name"]');
    const variable = variableInput?.value.trim().toLowerCase().replace(/[\s-]+/g, "_");
    const variableOk = variable === "part_time";
    const typeInput = root.querySelector('input[aria-label="Player fills: variable type"]');
    const typeOk = typeInput?.value.trim().toLowerCase() === "proxy";
    return Boolean(variableOk && typeOk);
  }, []);

  const validateCurrentScreen = useCallback(() => {
    const root = mainRef.current;
    if (!root) return true;

    const currentPuzzleSolved = Boolean(puzzlesSolved[String(screen)] ?? puzzleSolved);

    if (screen === 5 && !currentPuzzleSolved) {
      const variableInput = root.querySelector('input[aria-label="Developer fills: variable name"]');
        const variable = variableInput?.value.trim().toLowerCase().replace(/[\s-]+/g, "_");
      const variableOk = variable === "part_time";
      const typeInput = root.querySelector('input[aria-label="Player fills: variable type"]');
      const typeOk = typeInput?.value.trim().toLowerCase() === "proxy";
      if (!variableOk || !typeOk) {
        showStatus('Complete the Level 3 statement first: enter "part_time" and "proxy".');
        if (!variableOk) variableInput?.focus();
        else typeInput?.focus();
        return false;
      }
    }

    return true;
  }, [puzzleSolved, puzzlesSolved, screen, showStatus]);

  const reportHintUsed = useCallback((hintId) => {
    const socket = socketRef.current;
    if (!roomId || supervisorMode || !socket?.connected) return;
    socket.emit("useHint", { roomId, hintId, pageIndex: screen }, (response) => {
      if (!response?.success) {
        showStatus(response?.message || "That hint is not available yet.");
        return;
      }
      syncRoom(response);
    });
  }, [roomId, screen, showStatus, supervisorMode, syncRoom]);

  const markArchiveUnlocked = useCallback((answer) => {
    const socket = socketRef.current;
    if (!roomId || supervisorMode || !socket?.connected) {
      setPuzzleSolved(true);
      return;
    }

    socket.emit("solvePuzzle", { roomId, pageIndex: 3, answer, accepted: true }, (response) => {
      if (!response?.success) {
        showStatus(response?.message || "Archive unlocked locally, but the room did not sync yet.");
        return;
      }
      syncRoom(response);
      showStatus("Archive unlocked. Everyone can proceed to Level 2.");
    });
  }, [roomId, showStatus, supervisorMode, syncRoom]);
  const markHelenAccepted = useCallback((answer) => {
    const socket = socketRef.current;
    if (!roomId || supervisorMode || !socket?.connected) {
      setPuzzleSolved(true);
      return;
    }

    socket.emit("solvePuzzle", { roomId, pageIndex: 4, answer, accepted: true }, (response) => {
      if (!response?.success) {
        showStatus(response?.message || "Helen accepted it, but the room did not unlock yet.");
        return;
      }
      syncRoom(response);
      showStatus("Helen accepted the evidence. Everyone can proceed to Level 3.");
    });
  }, [roomId, showStatus, supervisorMode, syncRoom]);
  const markL4Recommendation = useCallback((recommendation) => {
    const socket = socketRef.current;
    if (!roomId || supervisorMode || !socket?.connected) {
      setPuzzleSolved(true);
      return;
    }
    socket.emit("solvePuzzle", { roomId, pageIndex: 6, answer: JSON.stringify(recommendation), accepted: true }, (response) => {
      if (!response?.success) {
        showStatus(response?.message || "That reason does not match the selected fairness metric.");
        return;
      }
      syncRoom(response);
      showStatus("Recommendation accepted. Everyone can proceed to Level 5.");
    });
  }, [roomId, showStatus, supervisorMode, syncRoom]);

  const markL5Announcement = useCallback((announcement) => {
    const socket = socketRef.current;
    if (!roomId || supervisorMode || !socket?.connected) {
      setPuzzleSolved(true);
      return;
    }
    socket.emit("solvePuzzle", { roomId, pageIndex: 7, answer: announcement, accepted: true }, (response) => {
      if (!response?.success) {
        showStatus(response?.message || "Could not submit the announcement.");
        return;
      }
      syncRoom(response);
      showStatus("Announcement submitted. Deployment is ready for the team.");
    });
  }, [roomId, showStatus, supervisorMode, syncRoom]);

  const joinRoom = useCallback(() => {
    const socket = socketRef.current;
    const codeInput = mainRef.current?.querySelector(".class-code-input");
    const groupInput = mainRef.current?.querySelector(".group-name-input");
    const requestedClassCode = codeInput?.value.trim().toUpperCase();
    const requestedGroupName = groupInput?.value.trim();

    if (!requestedClassCode) {
      showStatus("Enter a class code first.");
      codeInput?.focus();
      return;
    }
    if (!socket?.connected) {
      showStatus("The multiplayer server is not connected yet.");
      return;
    }

    if (requestedClassCode === "ADMIN") {
      const token = window.prompt("Enter your administrator or facilitator access key:");
      if (!token) return;
      adminTokenRef.current = token;
      setAdminToken(token);
      setRoomId("ADMIN");
      setSupervisorMode(true);
      setPendingAdvance(true);
      socket.emit("joinSupervisor", { token }, (response) => {
        setPendingAdvance(false);
        if (!response?.success) {
          setSupervisorMode(false); setRoomId(""); setAdminToken(""); adminTokenRef.current = "";
          showStatus(response?.message || "Could not open supervisor view.");
          return;
        }
        setSupervisorState(response);
        showStatus("Supervisor connected.");
      });
      return;
    }

    if (!requestedGroupName) {
      showStatus("Enter a group name first.");
      groupInput?.focus();
      return;
    }

    setSupervisorMode(false);
    setPendingAdvance(true);
    socket.emit("joinRoom", { classCode: requestedClassCode, groupName: requestedGroupName, pageIndex: screen, playerId: playerIdRef.current }, (response) => {
      setPendingAdvance(false);
      if (!response?.success) {
        showStatus(response?.message || "Could not join room.");
        return;
      }
      syncRoom(response);
      showStatus(response.ready ? "Rejoined a game already in progress." : `Joined ${response.groupName || "group"}. ${response.players} player(s) here now.`);
    });
  }, [screen, showStatus, syncRoom]);

  const createClass = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) { showStatus("The multiplayer server is not connected yet."); return; }
    const className = window.prompt("Name this class session (for example: AI Ethics, Monday 10:00):");
    if (!className?.trim()) return;
    socket.emit("createClass", { className }, (response) => {
      if (!response?.success) { showStatus(response?.message || "Could not create the class."); return; }
      setCreatedClass(response);
      const classInput = mainRef.current?.querySelector(".class-code-input");
      if (classInput) classInput.value = response.classCode;
      mainRef.current?.querySelector(".group-name-input")?.focus();
      showStatus("Class created. Save the facilitator dashboard key before sharing the code.");
    });
  }, [showStatus]);

  const startRoom = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected || !roomId) return;
    setPendingAdvance(true);
    socket.emit("startRoom", { roomId }, (response) => {
      setPendingAdvance(false);
      if (!response?.success) {
        showStatus(response?.message || "Could not start the game.");
        return;
      }
      syncRoom(response);
      showStatus("Game started.");
    });
  }, [roomId, showStatus, syncRoom]);

  const submitScreen = useCallback(() => {
    const socket = socketRef.current;
    if (!roomReady) {
      showStatus("Start the game on the join screen first.");
      return;
    }
    if (!roomId) {
      showStatus("Join a room before continuing.");
      return;
    }
    if (!socket?.connected) {
      showStatus("The multiplayer server is not connected.");
      return;
    }
    if (pendingAdvance) {
      showStatus("Your answer is submitted. Waiting for the rest of the team.");
      return;
    }
    if (!validateCurrentScreen()) return;

    const currentPuzzleSolved = Boolean(puzzlesSolved[String(screen)] ?? puzzleSolved);
    const solvedByThisPlayer = screen === 5 ? getLevel3PuzzleSolvedByThisPlayer() : false;
    if (screen === 5) {
      const root = mainRef.current;
      socket.emit("saveAnswer", { roomId, pageIndex: 5, field: "proxy_variable", value: root?.querySelector('input[aria-label="Developer fills: variable name"]')?.value || "" });
      socket.emit("saveAnswer", { roomId, pageIndex: 5, field: "variable_type", value: root?.querySelector('input[aria-label="Player fills: variable type"]')?.value || "" });
    }

    setPendingAdvance(true);
    socket.emit("submitPage", { roomId, pageIndex: screen, puzzleSolved: currentPuzzleSolved || solvedByThisPlayer }, (response) => {
      if (!response?.success) {
        setPendingAdvance(false);
        showStatus(response?.message || "Could not submit this screen.");
        return;
      }
      syncRoom(response);
      if (response.advanced) {
        setPendingAdvance(false);
        showStatus("Everyone submitted. Advancing together.");
      } else {
        showStatus(response.message || "Submitted. Waiting for the rest of the team.");
      }
    });
  }, [getLevel3PuzzleSolvedByThisPlayer, pendingAdvance, puzzleSolved, puzzlesSolved, roomId, roomReady, screen, showStatus, syncRoom, validateCurrentScreen]);

  const browsePage = useCallback((direction) => {
    setScreen(current => Math.max(0, Math.min(SCREEN_LABELS.length - 1, current + direction)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const next = useCallback(() => {
    if (reviewMode) { browsePage(1); return; }
    if (screen === 0) joinRoom();
    else if (screen < SCREEN_LABELS.length - 1) submitScreen();
  }, [browsePage, joinRoom, reviewMode, screen, submitScreen]);

  if (timeExpired && !reviewMode) {
    return <><style>{CSS}</style><AccessDeniedScreen onComplete={() => { reviewModeRef.current = true; setReviewMode(true); setStatusMessage("Time expired. Review mode is now active."); }} /></>;
  }

  if (supervisorMode) {
    return (
      <>
        <style>{CSS}</style>
        <main id="main" className="mockup-container" ref={mainRef}>
          <div className="room-status" role="status" aria-live="polite">
            <span>Supervisor</span>
            <span>Teams: {supervisorState?.teams?.length || 0}</span>
            <span className="global-timer" aria-label="Live status">Live</span>
            <span>{statusMessage}</span>
          </div>
          <ScreenSupervisor state={supervisorState} completedTeams={completedTeams} completedError={completedTeamsError} onRefresh={loadCompletedTeams} />
        </main>
      </>
    );
  }

  const screens = [
    <ScreenJoin   key={0} onNext={next} onCreateClass={createClass} createdClass={createdClass} hasJoined={players > 0} classInfo={classInfo} roomId={roomId} players={players} roomRoles={roomRoles} canStart={canStart} onStart={startRoom} />,
    <ScreenRole   key={1} onNext={next} selectedRole={playerRole} />,
    <ScreenIntro  key={2} onNext={next} />,
    playerRole === "hr"
      ? <ScreenL1HR key={3} onNext={next} onHintUsed={reportHintUsed} puzzleSolved={Boolean(puzzlesSolved["3"] ?? puzzleSolved)} onArchiveUnlocked={markArchiveUnlocked} />
      : <ScreenL1Dev key={3} onNext={next} onHintUsed={reportHintUsed} puzzleSolved={Boolean(puzzlesSolved["3"] ?? puzzleSolved)} onArchiveUnlocked={markArchiveUnlocked} />,
    <ScreenL2     key={4} onNext={next} onHintUsed={reportHintUsed} puzzleSolved={Boolean(puzzlesSolved["4"] ?? puzzleSolved)} onHelenAccepted={markHelenAccepted} onAnswerSave={(field, value) => saveAnswer(4, field, value)} />,
    playerRole === "hr"
      ? <ScreenL3HR key={5} onNext={next} onHintUsed={reportHintUsed} puzzleSolved={Boolean(puzzlesSolved["5"] ?? puzzleSolved)} />
      : <ScreenL3Dev key={5} onNext={next} onHintUsed={reportHintUsed} puzzleSolved={Boolean(puzzlesSolved["5"] ?? puzzleSolved)} />,
    <ScreenL4     key={6} onNext={next} onHintUsed={reportHintUsed} answers={roomAnswers["6"]} puzzleSolved={Boolean(puzzlesSolved["6"] ?? puzzleSolved)} onRecommendationSubmitted={markL4Recommendation} />,
    <ScreenL5     key={7} onNext={next} answers={roomAnswers["7"]} puzzleSolved={Boolean(puzzlesSolved["7"] ?? puzzleSolved)} playerRole={playerRole} onAnnouncementSubmitted={markL5Announcement} />,
    <ScreenComplete key={8} />,
  ];

  return (
    <>
      <style>{CSS}</style>
      <GameTimerContext.Provider value={{ time: timerStartedAt && Date.now() >= timerStartedAt ? formatTime(remainingSeconds) : null, levelStartedAt }}>
      {countdownStartedAt && <AccessCountdown startedAt={countdownStartedAt} />}
      {!cookieConsent && <CookieConsent onChoose={(choice) => { window.localStorage.setItem(COOKIE_CONSENT_KEY, choice); setCookieConsent(choice); }} />}
      <a className="skip" href="#main" style={{ position: "fixed", top: -100, left: 12, zIndex: 9999, background: "var(--brass-btn)", color: "var(--bg)", fontFamily: "var(--f-m)", fontSize: 12, textDecoration: "none", padding: "10px 18px" }}>
        Skip to main content
      </a>
      <main id="main" className="mockup-container" ref={mainRef}>
        <div className="mockup-title">Escape the Bias - Aethelon Level Flow</div>
        <div className="mockup-sub">Everyone in the same room progresses through each screen together</div>
        <div className="room-status" role="status" aria-live="polite">
          <span>{classInfo ? `Class: ${classInfo.className} ? Group: ${classInfo.groupName}` : `Room: ${roomId || "not joined"}`}</span>
          <span>Players: {players}</span>
          <span className="global-timer" aria-label="Time remaining">{formatTime(remainingSeconds)}</span>
          <span>{reviewMode ? "Review mode - puzzles bypassed" : statusMessage}</span>
        </div>
        <div className="screen-label">{SCREEN_LABELS[screen]}</div>
        {screens[screen]}
        {reviewMode && <SavedAnswers answers={roomAnswers} pageIndex={screen} />}
        {reviewMode && <div className="btn-row" style={{ padding: "24px 32px 40px", justifyContent: "space-between", borderTop: "1px solid rgba(255,255,255,.08)" }}>
          <button className="btn btn-ghost" onClick={() => browsePage(-1)} disabled={screen === 0}>Previous Page</button>
          <button className="btn btn-primary" onClick={() => browsePage(1)} disabled={screen === SCREEN_LABELS.length - 1}>Next Page</button>
        </div>}
      </main>
      </GameTimerContext.Provider>
    </>
  );
}
